import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashShell, KPI, StatusTag } from "@/components/dash";
import { toast } from "sonner";
import { LayoutDashboard, ClipboardCheck, Users, Banknote, MessageSquareWarning, FolderTree, UserCog, ScrollText, Settings, Loader2, Fingerprint, UploadCloud, X, Printer, Phone, Mail, MapPin, CheckCircle2, XCircle, ShieldCheck, AlertTriangle, FileText, Sparkles, Play, Save, HelpCircle, Trash2, Plus, ChevronUp, ChevronDown, Pencil, ImageOff, RefreshCw, GripVertical, Eye, Download, Activity, Zap, ExternalLink } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import RenderPreviewSwatch from "@/components/RenderPreviewSwatch";
import LiveClientPreview from "@/components/LiveClientPreview";
import RenderQueuePanel from "@/components/RenderQueuePanel";
import CadViewer from "@/components/CadViewer";
import PhotoManager from "@/components/PhotoManager";

const PERMS = [
  { key: "listings", label: "Listing Moderation" },
  { key: "disputes", label: "Dispute Support" },
  { key: "payouts", label: "Finance / Payouts" },
  { key: "users", label: "User Management" },
  { key: "content", label: "Content / Categories" },
];

const ALL_NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, perm: null },
  { key: "review_queue", label: "Review Queue", icon: ClipboardCheck, perm: "listings" },
  { key: "moderation", label: "Moderation", icon: ClipboardCheck, perm: "listings" },
  { key: "users", label: "Users", icon: Users, perm: "users" },
  { key: "finance", label: "Finance", icon: Banknote, perm: "payouts" },
  { key: "disputes", label: "Disputes", icon: MessageSquareWarning, perm: "disputes" },
  { key: "categories", label: "Categories", icon: FolderTree, perm: "content" },
  { key: "employees", label: "Employees", icon: UserCog, perm: "admin" },
  { key: "forensics", label: "Leak Forensics", icon: Fingerprint, perm: "admin" },
  { key: "photo_audit", label: "Photo Audit", icon: ImageOff, perm: "listings" },
  { key: "render_queue", label: "Render Queue", icon: Activity, perm: "listings" },
  { key: "vendor_waitlist", label: "CAM Waitlist", icon: Printer, perm: "users" },
  { key: "legal", label: "Legal & Grievance", icon: ShieldCheck, perm: "admin" },
  { key: "faq", label: "FAQ Editor", icon: HelpCircle, perm: "content" },
  { key: "audit", label: "Audit Log", icon: ScrollText, perm: "admin" },
  { key: "settings", label: "Settings", icon: Settings, perm: null },
];

// -------------------------------------------------------------- shared helpers
/**
 * Fetch the raw CAD file for a listing (auth-gated admin endpoint) and
 * trigger a browser download. Used by every admin surface that shows a
 * listing row so QA can pull the actual file the seller uploaded — no
 * separate detail page needed.
 */
async function downloadCad(listing, kind) {
  try {
    const { data } = await api.get(`/admin/listings/${listing.id}/download/${kind}`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    const safeTitle = (listing.title || "listing").replace(/[^\w\s.-]+/g, "_").slice(0, 60).trim();
    a.download = `${safeTitle}_${listing.id.slice(0, 8)}.${kind === "stl" ? "stl" : "3dm"}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (e) {
    toast.error(apiErr(e) || `Could not download ${kind.toUpperCase()}`);
  }
}


/**
 * AdminFilePreview
 * ----------------
 * Full-screen modal that streams the ORIGINAL STL or 3DM (no decimation,
 * no watermark) into the shared CadViewer so an admin can inspect the
 * exact file the seller uploaded — stray meshes, non-manifold edges,
 * unexpected units, etc. Backed by `GET /api/admin/listings/{id}/preview/{kind}`.
 */
function AdminFilePreview({ listing, kind, onClose }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("jwt") || "";
  // The CadViewer's fetch pipeline appends its own headers, so we
  // pass the URL WITH a short-lived token query so the admin auth
  // survives in <img>-style fetches too. Simpler: use the auth
  // header in a signed one-shot? For now the preview endpoint
  // accepts the Authorization header via the shared api client,
  // and CadViewer uses `fetch` under the hood which picks it up
  // when passed as `credentials: 'include'` — but we own the
  // fetch here. Read the file with `api` (auth headers) and hand
  // an object URL to CadViewer instead.
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    let localUrl = "";
    setBlobUrl(""); setErr("");
    api.get(`/admin/listings/${listing.id}/preview/${kind}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        localUrl = URL.createObjectURL(r.data);
        setBlobUrl(localUrl);
      })
      .catch((e) => { if (!cancelled) setErr(apiErr(e) || "Could not load preview"); });
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [listing.id, kind]);// eslint-disable-line

  const label = kind === "stl" ? "STL" : "3DM";
  return (
    <div
      className="fixed inset-0 z-[130] bg-slate-950/85 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="admin-file-preview-modal"
    >
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden border border-border">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <h2 className="font-display font-bold text-lg" data-testid="admin-preview-title">
              {label} preview · <span className="text-muted-foreground font-normal">{listing.title}</span>
            </h2>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Original file from the seller · no decimation · no watermark
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadCad(listing, kind)}
              className="border border-sky-500/40 text-sky-700 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-sky-500/5"
              data-testid={`admin-preview-download-${kind}`}
            >
              Download {label}
            </button>
            <button
              onClick={onClose}
              className="border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
              data-testid="admin-preview-close"
            >
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 bg-slate-100 dark:bg-slate-900" style={{ background: "#B8D8F0" }}>
          {err ? (
            <div className="h-full flex items-center justify-center p-6 text-center">
              <div>
                <p className="text-destructive font-semibold">{err}</p>
                <p className="text-xs text-muted-foreground mt-2">The file may have been evicted from local disk — try again in a few seconds after object-storage restore.</p>
              </div>
            </div>
          ) : !blobUrl ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              Loading {label}…
            </div>
          ) : kind === "stl" ? (
            <CadViewer listingId={listing.id} url={blobUrl} />
          ) : (
            <CadViewer listingId={listing.id} url="" dmUrl={blobUrl} />
          )}
        </div>
      </div>
    </div>
  );
}




export default function AdminDashboard() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin";
  const can = (perm) => isSuper || perm === null || (perm !== "admin" && user?.permissions?.includes(perm));
  const nav = ALL_NAV.filter((n) => can(n.perm));

  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || nav[0]?.key || "overview";
  const setTab = (t) => setParams({ tab: t });
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <DashShell title="Admin Console" subtitle={isSuper ? "Super Admin" : "Employee · " + (user?.permissions || []).join(", ")} nav={nav} active={active} onTab={setTab}>
      {active === "overview" && <Overview stats={stats} />}
      {active === "review_queue" && <ReviewQueue />}
      {active === "moderation" && <Moderation />}
      {active === "users" && <UsersTab />}
      {active === "finance" && <Finance />}
      {active === "disputes" && <Disputes />}
      {active === "categories" && <Categories />}
      {active === "employees" && isSuper && <Employees />}
      {active === "forensics" && isSuper && <Forensics />}
      {active === "photo_audit" && <PhotoAudit />}
      {active === "render_queue" && <RenderQueuePanel />}
      {active === "vendor_waitlist" && <VendorWaitlist />}
      {active === "legal" && isSuper && <LegalTab />}
      {active === "faq" && <FaqEditor />}
      {active === "audit" && isSuper && <Audit />}
      {active === "settings" && <SettingsTab isSuper={isSuper} />}
    </DashShell>
  );
}

function Overview({ stats }) {
  const [subs, setSubs] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions/analytics").then((r) => setSubs(r.data)).catch(() => setSubs(null)); }, []);

  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="GMV" value={inr(stats.gmv)} accent="text-primary" />
        <KPI label="Commission earned" value={inr(stats.commission)} accent="text-emerald-500" />
        <KPI label="Total orders" value={stats.total_orders} />
        <KPI label="Open disputes" value={stats.open_disputes} accent={stats.open_disputes ? "text-red-500" : ""} />
        <KPI label="Sellers" value={stats.sellers} />
        <KPI label="Buyers" value={stats.buyers} />
        <KPI label="Live listings" value={stats.listings} />
        <KPI label="Pending review" value={stats.pending_listings} accent={stats.pending_listings ? "text-amber-500" : ""} />
      </div>

      {/* --- Subscription revenue tile (MRR / ARR / grace count) --- */}
      {subs && (
        <div className="border border-border rounded-md bg-card p-5" data-testid="subs-analytics-tile">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Vendor subscriptions</p>
              <h3 className="font-display font-bold text-lg mt-1">
                Subscription revenue {!subs.subscriptions_enabled && <span className="ml-2 text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 align-middle">Not live yet</span>}
              </h3>
            </div>
            <a href="?tab=vendor_waitlist" className="text-xs text-primary hover:underline">Manage waitlist →</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="MRR" value={inr(subs.mrr)} accent="text-primary" />
            <KPI label="Projected ARR" value={inr(subs.arr)} />
            <KPI label="Active" value={subs.active_count} accent="text-emerald-500" />
            <KPI label="Trialing" value={subs.trialing_count} />
            <KPI label="Past due (grace)" value={subs.grace_count} accent={subs.grace_count ? "text-red-500" : ""} />
            <KPI label="Cancelled" value={subs.cancelled_count} />
            <KPI label="Expired" value={subs.expired_count} />
            <KPI label="Total shops" value={subs.active_count + subs.trialing_count + subs.grace_count} />
          </div>

          {subs.tier_breakdown.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tier breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {subs.tier_breakdown.map((t) => (
                  <div key={t.tier} className="border border-border rounded-md p-2.5 text-center">
                    <p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">{t.tier}</p>
                    <p className="font-display font-bold text-lg mt-1">{t.count}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">{inr(t.mrr)}/mo</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {subs.grace_vendors.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-mono uppercase tracking-widest text-red-500 mb-2">Vendors in grace period · {subs.grace_vendors.length}</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {subs.grace_vendors.map((g) => (
                  <div key={g.user_id} className="text-xs flex items-center justify-between border border-red-500/20 bg-red-500/5 rounded-md px-3 py-2">
                    <div>
                      <p className="font-medium">{g.name || "—"}</p>
                      <p className="text-muted-foreground font-mono">{g.email} · {g.tier}</p>
                    </div>
                    <span className="text-red-500 font-mono">{g.days_left} day{g.days_left === 1 ? "" : "s"} left</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border border-border rounded-md bg-card p-4">
        <p className="font-display font-semibold text-sm mb-4">GMV trend</p>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={stats.chart}>
            <defs><linearGradient id="gmv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
            <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Area type="monotone" dataKey="gmv" stroke="#2563EB" fill="url(#gmv)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// --- Review Queue: daily-scoped pending-listings tab
function ReviewQueue() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [reasons, setReasons] = useState({});
  const [modal, setModal] = useState(null);
  const [seoTarget, setSeoTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const load = () => api.get("/admin/review-queue", {
    params: { day: day || undefined, status: statusFilter },
  }).then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/admin/rejection-reasons").then((r) => setReasons(r.data)).catch(() => {}); }, [day, statusFilter]);

  const doAction = async (id, action, body = {}) => {
    try {
      await api.post(`/admin/listings/${id}/moderate`, { action, ...body });
      const msgs = {
        approve: "Listing approved — seller notified.",
        reject: "Listing rejected — seller notified.",
        delist: "Listing delisted — seller notified by email + in-app.",
      };
      toast.success(msgs[action] || `Listing ${action}ed`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const hardDelete = async (l) => {
    if (!window.confirm(`Permanently delete '${l.title}'? The seller will be emailed and in-app notified. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/listings/${l.id}`);
      toast.success("Deleted — seller has been notified by email.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const STATUSES = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "unpublished", label: "Delisted" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="space-y-5" data-testid="review-queue">
      {/* Status filter — new: show every status, not just pending */}
      <div className="flex flex-wrap items-center gap-2" data-testid="rq-status-tabs">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mr-2">Status</span>
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            data-testid={`rq-status-${s.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s.key ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-secondary"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Uploaded on</p>
          <div className="flex gap-2">
            <button
              onClick={() => setDay("")}
              data-testid="rq-day-all"
              className={`text-xs px-3 py-1.5 rounded-md border ${day === "" ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >
              Any date
            </button>
            <button
              onClick={() => setDay(today)}
              data-testid="rq-day-today"
              className={`text-xs px-3 py-1.5 rounded-md border ${day === today ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >
              Today
            </button>
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              data-testid="rq-day-picker"
              className="text-xs border border-border rounded-md px-3 py-1.5 bg-background"
            />
          </div>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Pending (all dates)</p>
          <p className="font-display font-extrabold text-3xl text-primary">{data?.total_pending ?? "—"}</p>
          <p className="text-[10px] text-muted-foreground font-mono mt-0.5">Showing {data?.shown_count ?? 0} in this filter</p>
        </div>
      </div>

      {data === null && <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>}
      {data && data.listings.length === 0 && (
        <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground text-sm">
          No listings match this filter.
        </div>
      )}

      {data && data.listings.map((l) => (
        <div key={l.id} className="border border-border rounded-md bg-card p-4 flex flex-wrap items-start gap-4" data-testid={`rq-row-${l.id}`}>
          {l.cover_image ? (
            <img src={l.cover_image} alt="" className="w-24 h-24 rounded-md object-cover shrink-0" />
          ) : (
            <div className="w-24 h-24 rounded-md bg-slate-900 mesh-grid shrink-0" />
          )}
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display font-semibold text-base">{l.title}</p>
              <StatusTag status={l.status} />
            </div>
            <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
              By <a href={`?tab=users&q=${encodeURIComponent(l.seller?.email || l.seller?.name || "")}`} className="text-primary hover:underline">{l.seller?.name}</a>
              {" · "}{l.file_type?.toUpperCase()}
              {" · "}{inr(l.price)}
              {" · "}{l.category_name}
              {" · uploaded "}{new Date(l.created_at).toLocaleString()}
            </p>
            {l.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{l.description}</p>}
            <a href={`/design/${l.id}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-block mt-2">Open preview →</a>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            {l.status === "pending" && (
              <button onClick={() => doAction(l.id, "approve")} data-testid={`rq-approve-${l.id}`} className="bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-emerald-700">Approve</button>
            )}
            <button onClick={() => setEditTarget(l)} data-testid={`rq-edit-${l.id}`} className="border border-border rounded-md px-3 py-2 text-sm font-semibold hover:bg-secondary flex items-center gap-1"><Pencil className="h-4 w-4" /> Edit</button>
            <button onClick={() => setSeoTarget(l)} data-testid={`rq-seo-${l.id}`} className="border border-primary/40 text-primary rounded-md px-3 py-2 text-sm font-semibold hover:bg-primary/5 flex items-center gap-1"><Sparkles className="h-4 w-4" /> SEO</button>
            {l.status === "approved" && (
              <button onClick={() => setModal({ listing: l, mode: "delist" })} data-testid={`rq-delist-${l.id}`} className="border border-amber-500/40 text-amber-600 rounded-md px-3 py-2 text-sm font-semibold hover:bg-amber-500/5">Delist w/ notice</button>
            )}
            {l.status === "pending" && (
              <button onClick={() => setModal({ listing: l, mode: "reject" })} data-testid={`rq-reject-${l.id}`} className="border border-red-500/40 text-red-600 rounded-md px-3 py-2 text-sm font-semibold hover:bg-red-500/5">Reject</button>
            )}
            <button onClick={() => hardDelete(l)} data-testid={`rq-delete-${l.id}`} className="border border-red-500/60 text-red-600 rounded-md px-3 py-2 text-sm font-semibold hover:bg-red-500/10 flex items-center gap-1"><Trash2 className="h-4 w-4" /> Delete</button>
          </div>
        </div>
      ))}

      {data && data.trend.length > 0 && (
        <div className="border border-border rounded-md bg-card p-4">
          <p className="font-display font-semibold text-sm mb-3">Pending uploads · last 14 days</p>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={data.trend}>
              <defs><linearGradient id="rq-trend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Area type="monotone" dataKey="count" stroke="#F59E0B" fill="url(#rq-trend)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {modal && <ReasonModal listing={modal.listing} mode={modal.mode} reasons={reasons} onClose={() => setModal(null)} onDone={(reason, note) => { doAction(modal.listing.id, modal.mode, { reason, note }); setModal(null); }} />}
      {seoTarget && <SeoEditorModal listing={seoTarget} onClose={() => setSeoTarget(null)} onSaved={() => { setSeoTarget(null); load(); }} />}
      {editTarget && <AdminEditListingModal listing={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
    </div>
  );
}

function Moderation() {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [reasons, setReasons] = useState({});
  const [modal, setModal] = useState(null);       // {listing, mode: 'reject' | 'delist'}
  const [seoTarget, setSeoTarget] = useState(null); // listing being SEO-edited
  const [editTarget, setEditTarget] = useState(null); // listing being full-edited
  const [previewTarget, setPreviewTarget] = useState(null); // {listing, kind:'stl'|'dm'}
  const load = () => api.get("/admin/listings", {
    params: {
      status: filter === "needs_review" ? undefined : (filter || undefined),
      needs_review: filter === "needs_review" ? true : undefined,
    },
  }).then((r) => setListings(r.data));
  useEffect(() => { load(); api.get("/admin/rejection-reasons").then((r) => setReasons(r.data)).catch(() => {}); }, [filter]);

  const doAction = async (id, action, body = {}) => {
    try {
      await api.post(`/admin/listings/${id}/moderate`, { action, ...body });
      toast.success(`Listing ${action}${action.endsWith("e") ? "d" : "ed"}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const hardDelete = async (l) => {
    const ok = window.confirm(
      `PERMANENTLY delete "${l.title}"?\n\nThis wipes the listing, its STL / 3DM / studio photos / rotation videos from disk, and clears any wishlist entries.\n\nThis cannot be undone. Use "Delist" instead if the listing has already been sold.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/listings/${l.id}`);
      toast.success(`Deleted — ${data.files_removed} file(s) removed from disk`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const togglePin = async (l) => {
    try {
      const { data } = await api.post(`/admin/listings/${l.id}/pin`);
      toast.success(data.pinned
        ? `Pinned "${l.title}" to the top of the marketplace.`
        : `Unpinned "${l.title}".`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {["pending", "approved", "rejected", "unpublished", "needs_review", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s || "all"}`}
            className={`text-sm px-3 py-1.5 rounded-md border ${filter === s ? (s === "needs_review" ? "border-red-500 text-red-600 bg-red-50" : "border-primary text-primary bg-primary/5") : (s === "needs_review" ? "border-red-300 text-red-500 hover:bg-red-50" : "border-border text-muted-foreground")}`}
          >
            {s === "needs_review" ? "⚠ Needs CAD review" : s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {listings.map((l) => (
          <div key={l.id} className="border border-border rounded-md bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`mod-row-${l.id}`}>
            {l.cover_image && <img src={l.cover_image} alt="" className="w-14 h-14 rounded object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{l.title}</p>
              <p className="font-mono text-xs text-muted-foreground truncate">{l.seller?.name} · {l.file_type?.toUpperCase()} · {inr(l.price)} · {l.category_name}</p>
              {l.needs_admin_review && (
                <div className="mt-1 inline-flex items-center gap-2 rounded border border-red-500/60 bg-red-500/10 text-red-700 text-[11px] font-mono uppercase tracking-widest px-2 py-1" data-testid={`needs-review-${l.id}`}>
                  ⚠ Needs CAD review — verify STL/3DM matches the seller's photos
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${l.id}/download/stl`}
                    target="_blank" rel="noreferrer"
                    className="ml-1 underline hover:no-underline"
                    data-testid={`download-stl-review-${l.id}`}
                  >Download STL</a>
                  {l.dm_file && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${l.id}/download/dm`}
                      target="_blank" rel="noreferrer"
                      className="underline hover:no-underline"
                      data-testid={`download-dm-review-${l.id}`}
                    >Download 3DM</a>
                  )}
                  {/* 2026-02-28 — one-click delist for CAD mismatch. Fires
                      the standard moderate/delist path with a preset
                      reason=cad_mismatch, so the seller gets the
                      dedicated notification + email explaining exactly
                      why. No modal, no note dialog — admin has already
                      inspected the file via the download link above. */}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delist "${l.title}" for CAD mismatch? The seller will be notified with a specific reason and can re-upload.`)) return;
                      try {
                        await api.post(`/admin/listings/${l.id}/moderate`, {
                          action: "delist",
                          reason: "cad_mismatch",
                          note: "The uploaded photos do not depict the same object as the CAD file. Re-render the design in your browser (Regenerate) or upload photos of the actual model before relisting.",
                        });
                        toast.success(`Delisted for CAD mismatch — seller notified.`);
                        load();
                      } catch (e) { toast.error(apiErr(e)); }
                    }}
                    data-testid={`delist-cad-mismatch-${l.id}`}
                    className="ml-1 rounded border border-red-500 bg-red-600 text-white px-2 py-0.5 hover:bg-red-700 font-mono uppercase tracking-widest text-[10px]"
                  >
                    Delist · CAD mismatch
                  </button>
                </div>
              )}
              {l.reject_reason && <p className="text-[11px] text-red-500 font-mono mt-1">reject: {reasons[l.reject_reason] || l.reject_reason}{l.reject_note ? ` — ${l.reject_note}` : ""}</p>}
              {l.delisted_reason && <p className="text-[11px] text-amber-600 font-mono mt-1">delisted: {reasons[l.delisted_reason] || l.delisted_reason}{l.delisted_note ? ` — ${l.delisted_note}` : ""}</p>}
            </div>
            <StatusTag status={l.status} />
            {l.status === "pending" && (
              <div className="flex gap-2">
                <button onClick={() => doAction(l.id, "approve")} data-testid={`approve-${l.id}`} className="bg-emerald-600 text-white rounded-md px-3 py-1.5 text-sm hover:bg-emerald-700">Approve</button>
                <button onClick={() => setModal({ listing: l, mode: "reject" })} data-testid={`reject-${l.id}`} className="border border-red-500/40 text-red-600 rounded-md px-3 py-1.5 text-sm hover:bg-red-500/5">Reject</button>
                <button onClick={() => setSeoTarget(l)} data-testid={`seo-${l.id}`} className="border border-primary/40 text-primary rounded-md px-3 py-1.5 text-sm hover:bg-primary/5 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> SEO</button>
              </div>
            )}
            {l.status === "approved" && (
              <div className="flex gap-2">
                <button onClick={() => togglePin(l)} data-testid={`pin-${l.id}`} title={l.pinned ? "Unpin from marketplace top" : "Pin to marketplace top"} className={`border rounded-md px-3 py-1.5 text-sm flex items-center gap-1 ${l.pinned ? "border-amber-500 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20" : "border-border hover:border-amber-500 hover:text-amber-600"}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={l.pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>
                  {l.pinned ? "Pinned" : "Pin"}
                </button>
                <button onClick={() => doAction(l.id, "feature")} className="border border-border rounded-md px-3 py-1.5 text-sm hover:border-primary">{l.featured ? "Unfeature" : "Feature"}</button>
                <button onClick={() => setSeoTarget(l)} data-testid={`seo-${l.id}`} className="border border-primary/40 text-primary rounded-md px-3 py-1.5 text-sm hover:bg-primary/5 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> SEO</button>
                <button onClick={() => setModal({ listing: l, mode: "delist" })} data-testid={`delist-${l.id}`} className="border border-amber-500/40 text-amber-600 rounded-md px-3 py-1.5 text-sm hover:bg-amber-500/5">Delist</button>
              </div>
            )}
            {l.status === "unpublished" && (
              <div className="flex gap-2">
                <button onClick={() => setSeoTarget(l)} data-testid={`seo-${l.id}`} className="border border-primary/40 text-primary rounded-md px-3 py-1.5 text-sm hover:bg-primary/5 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> SEO</button>
                <button onClick={() => doAction(l.id, "unlist")} data-testid={`relist-${l.id}`} className="border border-emerald-500/40 text-emerald-600 rounded-md px-3 py-1.5 text-sm hover:bg-emerald-500/5">Relist</button>
              </div>
            )}
            {/* Universal admin actions — visible on every status */}
            <div className="flex gap-2 flex-wrap">
              {l.stl_file && (
                <>
                  <button
                    onClick={() => setPreviewTarget({ listing: l, kind: "stl" })}
                    data-testid={`preview-stl-${l.id}`}
                    className="border border-primary/40 text-primary rounded-md px-3 py-1.5 text-sm hover:bg-primary/5 flex items-center gap-1"
                    title="Open the seller's raw STL in a 3D viewer"
                  >
                    <Eye className="h-3.5 w-3.5" /> STL
                  </button>
                  <button
                    onClick={() => downloadCad(l, "stl")}
                    data-testid={`download-stl-${l.id}`}
                    className="border border-sky-500/40 text-sky-700 rounded-md px-3 py-1.5 text-sm hover:bg-sky-500/5 flex items-center gap-1"
                    title="Download the raw STL for QA scrutiny"
                  >
                    <Download className="h-3.5 w-3.5" /> STL
                  </button>
                </>
              )}
              {l.dm_file && (
                <>
                  <button
                    onClick={() => setPreviewTarget({ listing: l, kind: "dm" })}
                    data-testid={`preview-3dm-${l.id}`}
                    className="border border-primary/40 text-primary rounded-md px-3 py-1.5 text-sm hover:bg-primary/5 flex items-center gap-1"
                    title="Open the seller's raw 3DM in a 3D viewer"
                  >
                    <Eye className="h-3.5 w-3.5" /> 3DM
                  </button>
                  <button
                    onClick={() => downloadCad(l, "dm")}
                    data-testid={`download-3dm-${l.id}`}
                    className="border border-sky-500/40 text-sky-700 rounded-md px-3 py-1.5 text-sm hover:bg-sky-500/5 flex items-center gap-1"
                    title="Download the raw 3DM for QA scrutiny"
                  >
                    <Download className="h-3.5 w-3.5" /> 3DM
                  </button>
                </>
              )}
              <button
                onClick={() => setEditTarget(l)}
                data-testid={`edit-${l.id}`}
                className="border border-slate-500/40 text-slate-700 rounded-md px-3 py-1.5 text-sm hover:bg-slate-500/5 flex items-center gap-1"
                title="Edit title, description, price, category…"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => hardDelete(l)}
                data-testid={`delete-${l.id}`}
                className="border border-red-500/40 text-red-600 rounded-md px-3 py-1.5 text-sm hover:bg-red-500/5 flex items-center gap-1"
                title="Permanently delete (wipes files from disk)"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
        {listings.length === 0 && <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground">Nothing here.</div>}
      </div>
      {modal && <ReasonModal listing={modal.listing} mode={modal.mode} reasons={reasons} onClose={() => setModal(null)} onDone={(reason, note) => { doAction(modal.listing.id, modal.mode, { reason, note }); setModal(null); }} />}
      {seoTarget && <SeoEditorModal listing={seoTarget} onClose={() => setSeoTarget(null)} onSaved={() => { setSeoTarget(null); load(); }} />}
      {editTarget && <AdminEditListingModal listing={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      {previewTarget && (
        <AdminFilePreview
          listing={previewTarget.listing}
          kind={previewTarget.kind}
          onClose={() => setPreviewTarget(null)}
        />
      )}
    </div>
  );
}

// Full-field admin edit — title, description, price, category, tags, weight.
// Backend: PUT /api/admin/listings/{id}
function AdminEditListingModal({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: listing.title || "",
    description: listing.description || "",
    price: listing.price ?? 0,
    category_id: listing.category_id || "",
    tags: (listing.tags || []).join(", "),
    ring_size: listing.ring_size || "",
    cam_weight: listing.cam_weight ?? "",
  });
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        price: Number(form.price),
        category_id: form.category_id || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        ring_size: form.ring_size || null,
        cam_weight: form.cam_weight === "" ? null : Number(form.cam_weight),
      };
      await api.put(`/admin/listings/${listing.id}`, payload);
      toast.success("Listing updated");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[9997] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6" data-testid="admin-edit-modal">
        <h3 className="font-display font-bold text-lg">Edit listing</h3>
        <p className="text-xs text-muted-foreground font-mono mt-1 truncate">{listing.seller?.name} · {listing.id.slice(0, 8)}</p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Title</span>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="edit-description" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Price (INR)</span>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-price" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">CAM weight (g)</span>
              <input type="number" step="0.01" value={form.cam_weight} onChange={(e) => setForm({ ...form, cam_weight: e.target.value })} data-testid="edit-weight" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Category</span>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="edit-category" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background">
                <option value="">— None —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Ring size (IN)</span>
              <input type="text" value={form.ring_size} onChange={(e) => setForm({ ...form, ring_size: e.target.value })} data-testid="edit-ringsize" placeholder="e.g. 12" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Tags (comma separated)</span>
            <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="edit-tags" placeholder="solitaire, engagement, six-prong" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={() => setPhotosOpen(true)}
            data-testid="edit-manage-photos"
            className="mr-auto border border-primary/50 text-primary rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/5 flex items-center gap-1.5"
          >
            <Sparkles className="h-4 w-4" /> Manage photos
          </button>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="edit-submit" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
      {/* Render PhotoManager OUTSIDE this Edit modal to avoid z-index +
          click-propagation collisions. Also bumps its z-index to 9998
          (Edit modal sits at 9997) and passes the correct `onSaved`
          prop (was mis-named `onChanged` — meant PhotoManager's
          publish/regenerate hooks never refreshed the admin table). */}
      {photosOpen && (
        <div className="fixed inset-0 z-[9998]">
          <PhotoManager
            listing={listing}
            onClose={() => setPhotosOpen(false)}
            onSaved={() => { onSaved && onSaved(); }}
          />
        </div>
      )}
    </div>
  );
}

// Reusable modal to capture a rejection or delist reason. Used from both
// Moderation and Review Queue tabs so the seller always gets a clear reason.
function ReasonModal({ listing, mode, reasons, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const label = mode === "reject" ? "Reject" : "Delist";
  const noun = mode === "reject" ? "rejection" : "delisting";
  const submit = () => {
    if (!reason) return toast.error("Please pick a reason");
    onDone(reason, note.trim() || null);
  };
  return (
    <div className="fixed inset-0 z-[9997] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6" data-testid={`reason-modal-${mode}`}>
        <h3 className="font-display font-bold text-lg">{label} listing</h3>
        <p className="text-xs text-muted-foreground font-mono mt-1 truncate">{listing.title}</p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Reason for {noun}</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-select" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background">
              <option value="">— Choose —</option>
              {Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Note to seller (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="reason-note" placeholder="Add a short line so the seller understands what to fix." className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} data-testid="reason-submit" className={`rounded-md px-4 py-2 text-sm font-semibold text-white ${mode === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}>
            {label} & notify seller
          </button>
        </div>
      </div>
    </div>
  );
}

// SEO Editor — lets admin/content editors update title, description, tags,
// meta title/description, and URL slug on any listing. Backend: POST
// /api/admin/listings/{id}/seo (with a full audit-log diff).
function SeoEditorModal({ listing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: listing.title || "",
    description: listing.description || "",
    tags: (listing.tags || []).join(", "),
    meta_title: listing.meta_title || "",
    meta_description: listing.meta_description || "",
    slug: listing.slug || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim() || null,
        description: form.description.trim() || null,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : null,
        meta_title: form.meta_title.trim() || null,
        meta_description: form.meta_description.trim() || null,
        slug: form.slug.trim() || null,
      };
      const { data } = await api.post(`/admin/listings/${listing.id}/seo`, payload);
      if (data.changed) {
        toast.success(`SEO saved — ${data.changed} field${data.changed > 1 ? "s" : ""} updated.`);
      } else {
        toast.info("No changes to save.");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[9997] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seo-editor-modal">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-display font-bold text-lg">SEO / Content editor</h3>
              <p className="text-xs text-muted-foreground font-mono truncate max-w-[400px]">{listing.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary" data-testid="seo-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
            Edits here only touch product copy and metadata — the seller's files, price, and category are never modified. Every change is logged to the audit trail.
          </p>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Product title</span>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="seo-title" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} data-testid="seo-description" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-sans" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Tags / keywords (comma-separated)</span>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="seo-tags" placeholder="ring, bridal, 18k, diamond-halo" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Meta title</span>
              <input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} data-testid="seo-meta-title" placeholder="60 char limit" maxLength={60} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
              <span className="text-[10px] font-mono text-muted-foreground">{form.meta_title.length}/60</span>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">URL slug</span>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="seo-slug" placeholder="18k-diamond-halo-ring" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Meta description</span>
            <textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} maxLength={160} data-testid="seo-meta-description" placeholder="Shown on Google search results — 160 char limit" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-sans" />
            <span className="text-[10px] font-mono text-muted-foreground">{form.meta_description.length}/160</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="seo-save" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}



function UsersTab() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const [flagged, setFlagged] = useState(false);
  const [q, setQ] = useState("");
  const [pendingQ, setPendingQ] = useState("");
  const [sellerModal, setSellerModal] = useState(null);
  const [detailsUid, setDetailsUid] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // {id, email, name} — opens the confirm modal
  const load = () => api.get("/admin/users", { params: { role: role || undefined, q: q || undefined, flagged: flagged || undefined } }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, [role, q, flagged]);
  const suspend = async (id) => { await api.post(`/admin/users/${id}/suspend`); load(); };
  const kyc = async (id, action) => { await api.post(`/admin/users/${id}/kyc?action=${action}`); toast.success(`KYC ${action}d`); load(); };
  const canSell = (u) => u.role === "seller" || u.role === "both";
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex gap-2 flex-wrap">
          {["", "buyer", "seller", "both", "cam_vendor"].map((r) => (
            <button
              key={r || "all"}
              onClick={() => { setRole(r); setFlagged(false); }}
              data-testid={`users-role-${r || "all"}`}
              className={`text-sm px-3 py-1.5 rounded-md border ${role === r && !flagged ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}
            >
              {r ? r.replace("_", " ") : "All"}
            </button>
          ))}
          <button
            onClick={() => { setFlagged(!flagged); setRole(""); }}
            data-testid="users-filter-flagged"
            className={`text-sm px-3 py-1.5 rounded-md border font-semibold ${flagged ? "border-red-500 text-white bg-red-600" : "border-red-300 text-red-700 bg-red-50 hover:bg-red-100"}`}
          >
            ⚠ Fraud flagged
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setQ(pendingQ.trim()); }}
          className="flex-1 flex gap-2 min-w-[240px]"
          data-testid="users-search-form"
        >
          <input
            value={pendingQ}
            onChange={(e) => setPendingQ(e.target.value)}
            placeholder="Search by name, email, mobile or user id"
            data-testid="users-search-input"
            className="flex-1 border border-border rounded-md p-2 text-sm bg-background"
          />
          <button data-testid="users-search-btn" className="border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary">Search</button>
          {q && <button type="button" onClick={() => { setQ(""); setPendingQ(""); }} className="text-xs text-muted-foreground">Clear</button>}
        </form>
      </div>
      <div className="border border-border rounded-md bg-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left"><tr>{["Name", "Email", "Mobile", "Role", "KYC", "Actions"].map((h) => <th key={h} className="px-4 py-2.5 text-[11px] uppercase font-mono text-muted-foreground">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-t border-border ${u.suspended ? "opacity-50" : ""} ${u.fraud_flag ? "bg-red-50/40" : ""}`}>
                <td className="px-4 py-2.5 font-medium">
                  <div className="flex items-center gap-2">
                    <span>{u.name}</span>
                    {u.fraud_flag?.reason === "cad_mismatch_repeat_offender" && (
                      <span
                        data-testid={`fraud-chip-${u.id}`}
                        title={`Auto-suspended after ${u.fraud_flag.count_30d} CAD-mismatch delists in 30 days`}
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white whitespace-nowrap"
                      >
                        ⚠ CAD-mismatch ×{u.fraud_flag.count_30d}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{u.phone || u.mobile || "—"}</td>
                <td className="px-4 py-2.5 uppercase text-xs">{u.role}</td>
                <td className="px-4 py-2.5"><StatusTag status={u.kyc_status === "none" ? "pending" : u.kyc_status} /></td>
                <td className="px-4 py-2.5 flex gap-2 flex-wrap">
                  <button
                    onClick={() => setDetailsUid(u.id)}
                    data-testid={`users-details-${u.id}`}
                    className="text-xs border border-primary/40 text-primary rounded px-2 py-1 hover:bg-primary/5 flex items-center gap-1"
                  >
                    <Eye className="h-3 w-3" /> Details
                  </button>
                  {canSell(u) && (
                    <button
                      onClick={() => setSellerModal(u)}
                      data-testid={`users-view-listings-${u.id}`}
                      className="text-xs border border-primary/40 text-primary rounded px-2 py-1 hover:bg-primary/5"
                    >
                      View listings
                    </button>
                  )}
                  {u.role === "seller" && u.kyc_status === "pending" && <button onClick={() => kyc(u.id, "approve")} className="text-xs bg-emerald-600 text-white rounded px-2 py-1">Verify</button>}
                  <button onClick={() => suspend(u.id)} data-testid={`users-suspend-${u.id}`} className="text-xs border border-border rounded px-2 py-1 hover:border-red-500 hover:text-red-500">{u.suspended ? "Unsuspend" : "Suspend"}</button>
                  {u.role !== "admin" && (
                    <button
                      onClick={() => setDeleteTarget(u)}
                      data-testid={`users-delete-${u.id}`}
                      className="text-xs border border-red-300 text-red-700 rounded px-2 py-1 hover:bg-red-50 font-semibold"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sellerModal && (
        <SellerListingsModal seller={sellerModal} onClose={() => setSellerModal(null)} />
      )}
      {detailsUid && (
        <AdminUserDetailsModal uid={detailsUid} onClose={() => setDetailsUid(null)} />
      )}
      {deleteTarget && (
        <DeleteUserModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); }}
        />
      )}
    </div>
  );
}

// 2026-03-01 — Hard-delete confirmation modal. Requires the admin to
// retype the victim's email + provide a written reason (audit-logged).
function DeleteUserModal({ target, onClose, onDeleted }) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const emailMatches = confirmEmail.trim().toLowerCase() === (target.email || "").toLowerCase();
  const canDelete = emailMatches && reason.trim().length >= 6 && !busy;
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.id}`, {
        data: { reason: reason.trim(), confirm_email: confirmEmail.trim() },
      });
      toast.success(`${target.email} has been deleted.`);
      onDeleted();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="delete-user-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-red-200 bg-red-50 rounded-t-lg flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-red-900">⚠ Delete user permanently</h3>
          <button onClick={onClose} className="text-red-700 hover:text-red-900 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="border border-red-300 bg-red-50 rounded-md p-3 text-xs text-red-900 space-y-1">
            <p className="font-semibold">This deletes {target.name || target.email} and their personal data.</p>
            <p>• Their draft / pending listings are erased.</p>
            <p>• Their published listings are unpublished (paid buyers keep access).</p>
            <p>• Orders / disputes / reviews are preserved for records (name becomes "Deleted user").</p>
            <p>• Wishlist, cart, notifications, KYC docs, payout methods, appeals — all wiped.</p>
            <p className="font-semibold mt-1">This action is irreversible.</p>
          </div>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Retype the user's email to confirm</span>
            <input
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={target.email}
              data-testid="delete-user-confirm-email"
              className={`mt-1 w-full border rounded-md p-2.5 text-sm font-mono focus:outline-none ${confirmEmail && !emailMatches ? "border-red-400 bg-red-50" : "border-border bg-white focus:border-primary"}`}
            />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Reason (audit-logged)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder='e.g. "GDPR-style deletion request received via email on 2026-03-01"'
              data-testid="delete-user-reason"
              className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-white focus:outline-none focus:border-primary"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary">Cancel</button>
            <button
              onClick={submit}
              disabled={!canDelete}
              data-testid="delete-user-submit"
              className="bg-red-700 text-white rounded-md px-5 py-2 text-sm font-semibold hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// SellerListingsModal — pops up when admin clicks "View listings" on a
// seller row. Shows the whole catalog + status counts + inline Edit /
// Delist / Delete actions so admins can act on any listing without
// leaving the Users tab.
function SellerListingsModal({ seller, onClose }) {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reasons, setReasons] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [modal, setModal] = useState(null);

  const load = () => {
    setData(null);
    api.get(`/admin/users/${seller.id}/listings`).then((r) => setData(r.data)).catch(() => setData({ listings: [], counts: {}, total: 0 }));
  };
  useEffect(() => { load(); api.get("/admin/rejection-reasons").then((r) => setReasons(r.data)).catch(() => {}); }, [seller.id]);

  const doAction = async (id, action, body = {}) => {
    try {
      await api.post(`/admin/listings/${id}/moderate`, { action, ...body });
      toast.success(action === "delist" ? "Delisted — seller notified by email + in-app." : `Listing ${action}ed`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const hardDelete = async (l) => {
    if (!window.confirm(`Permanently delete '${l.title}'? Seller will be emailed and in-app notified.`)) return;
    try {
      await api.delete(`/admin/listings/${l.id}`);
      toast.success("Deleted — seller notified by email.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = data?.listings?.filter((l) => statusFilter === "all" || l.status === statusFilter) || [];

  return (
    <div
      className="fixed inset-0 z-[9996] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="seller-listings-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display font-bold text-lg">{seller.name}'s listings</h3>
            <p className="text-xs text-muted-foreground truncate">{seller.email} · {seller.role}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary" data-testid="seller-listings-close"><X className="h-4 w-4" /></button>
        </div>

        {!data ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading catalog…</div>
        ) : (
          <>
            {/* Summary chips */}
            <div className="px-5 py-3 bg-secondary/30 border-b border-border flex flex-wrap gap-2 items-center">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mr-2">Total {data.total}</span>
              {["all", "approved", "pending", "unpublished", "rejected"].map((k) => {
                const n = k === "all" ? data.total : (data.counts?.[k] || 0);
                return (
                  <button
                    key={k}
                    onClick={() => setStatusFilter(k)}
                    data-testid={`sl-filter-${k}`}
                    className={`text-xs px-3 py-1 rounded-full border ${statusFilter === k ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:bg-secondary"}`}
                  >
                    {k} · {n}
                  </button>
                );
              })}
            </div>

            <div className="p-4 space-y-3">
              {filtered.length === 0 ? (
                <div className="border border-dashed border-border rounded-md py-14 text-center text-muted-foreground text-sm">
                  No listings in this filter.
                </div>
              ) : filtered.map((l) => (
                <div key={l.id} className="border border-border rounded-md p-3 flex items-start gap-3" data-testid={`sl-row-${l.id}`}>
                  {l.cover_image ? (
                    <img src={l.cover_image} alt="" className="w-16 h-16 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-slate-900 mesh-grid shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm truncate">{l.title}</p>
                      <StatusTag status={l.status} />
                    </div>
                    <p className="text-[11px] font-mono text-muted-foreground mt-0.5">
                      {l.file_type?.toUpperCase()} · {inr(l.price)} · {l.category_name} · {new Date(l.created_at).toLocaleDateString()}
                    </p>
                    <a href={`/design/${l.id}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">Open preview →</a>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    <button onClick={() => setEditTarget(l)} data-testid={`sl-edit-${l.id}`} className="text-xs border border-border rounded px-2 py-1 hover:bg-secondary flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                    {l.status === "approved" && (
                      <button onClick={() => setModal({ listing: l, mode: "delist" })} data-testid={`sl-delist-${l.id}`} className="text-xs border border-amber-500/40 text-amber-600 rounded px-2 py-1 hover:bg-amber-500/5">Delist</button>
                    )}
                    {l.status === "pending" && (
                      <button onClick={() => doAction(l.id, "approve")} data-testid={`sl-approve-${l.id}`} className="text-xs bg-emerald-600 text-white rounded px-2 py-1 hover:bg-emerald-700">Approve</button>
                    )}
                    <button onClick={() => hardDelete(l)} data-testid={`sl-delete-${l.id}`} className="text-xs border border-red-500/60 text-red-600 rounded px-2 py-1 hover:bg-red-500/10 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {modal && <ReasonModal listing={modal.listing} mode={modal.mode} reasons={reasons} onClose={() => setModal(null)} onDone={(reason, note) => { doAction(modal.listing.id, modal.mode, { reason, note }); setModal(null); }} />}
        {editTarget && <AdminEditListingModal listing={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      </div>
    </div>
  );
}


// AdminUserDetailsModal — full contact/KYC/activity dossier surfaced from
// GET /api/admin/users/{uid}/details. Opened by the new "Details" button
// on every UsersTab row so ops can triage without navigating tabs.
function AdminUserDetailsModal({ uid, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [appeal, setAppeal] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [showManualVerify, setShowManualVerify] = useState(false);
  const [manualVerifyReason, setManualVerifyReason] = useState("");
  const [manualVerifying, setManualVerifying] = useState(false);
  // 2026-03-01 — PAN viewer state. `panSession` holds the download token
  // after the admin passes TOTP; while it's live we can render the image
  // + call the /decrypted endpoint without re-prompting.
  const [twofaEnabled, setTwofaEnabled] = useState(false);
  const [showTotpPrompt, setShowTotpPrompt] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);
  const [panSession, setPanSession] = useState(null); // {token, expires_at}
  const [decryptedPan, setDecryptedPan] = useState(null);
  const loadAll = () => {
    api.get(`/admin/users/${uid}/details`).then((r) => setData(r.data)).catch((e) => setError(apiErr(e)));
    api.get(`/admin/appeals`, { params: { status: "all" } })
      .then((r) => setAppeal((r.data || []).find((a) => a.user_id === uid && a.status === "pending") || null))
      .catch(() => setAppeal(null));
    api.get("/admin/2fa/status").then((r) => setTwofaEnabled(!!r.data?.enabled)).catch(() => setTwofaEnabled(false));
  };
  useEffect(() => { loadAll(); }, [uid]);
  const authorisePan = async () => {
    if (!/^\d{6}$/.test(totpCode)) { toast.error("Enter the 6-digit code from your authenticator app."); return; }
    setTotpBusy(true);
    try {
      const { data: r } = await api.post(`/admin/kyc/${uid}/pan-image/authorize`, { totp_code: totpCode });
      // Two-token flow: `authorize` returns a single-use token. To keep
      // the UX one-shot for the admin, we immediately call `/decrypted`
      // (which also consumes it), then request a FRESH one for the
      // image download button so both actions work without another prompt.
      const decRes = await api.get(`/admin/kyc/${uid}/decrypted`, { params: { token: r.token } });
      setDecryptedPan(decRes.data);
      // Second authorize for the image download button
      const { data: r2 } = await api.post(`/admin/kyc/${uid}/pan-image/authorize`, { totp_code: totpCode });
      setPanSession({ token: r2.token });
      setShowTotpPrompt(false);
      setTotpCode("");
      toast.success("Authorised — you can now view the PAN details");
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setTotpBusy(false); }
  };
  const openPanImage = () => {
    if (!panSession?.token) { toast.error("Please re-authorise — the download link expired."); return; }
    const url = `${window.__jx_apiRoot || ""}/api/admin/kyc/${uid}/pan-image/download?token=${panSession.token}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setPanSession(null); // single-use token now consumed
  };
  const resolve = async (action) => {
    if (!appeal) return;
    if (action === "reject" && rejectNote.trim().length < 6) {
      toast.error("Please add a short note (min 6 chars) for the seller.");
      return;
    }
    setResolving(true);
    try {
      await api.post(`/admin/appeals/${appeal.id}/resolve`, {
        action, note: action === "reject" ? rejectNote.trim() : null,
      });
      toast.success(action === "approve" ? "Appeal approved — seller reinstated" : "Appeal rejected");
      loadAll();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setResolving(false);
    }
  };
  const manualVerifyMobile = async () => {
    if (manualVerifyReason.trim().length < 6) {
      toast.error("Please add a reason (min 6 chars) for the audit log.");
      return;
    }
    setManualVerifying(true);
    try {
      await api.post(`/admin/users/${uid}/verify-mobile-manual`, { reason: manualVerifyReason.trim() });
      toast.success("Mobile marked as verified");
      setShowManualVerify(false);
      setManualVerifyReason("");
      loadAll();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setManualVerifying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9997] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="user-details-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-display font-bold text-lg">
                {data?.user?.name || "Loading…"}
              </h3>
              <p className="text-xs text-muted-foreground truncate max-w-[400px]">
                {data?.user?.email}{data?.user?.role ? ` · ${data.user.role}` : ""}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary" data-testid="user-details-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && <div className="p-6 text-sm text-red-600">{error}</div>}
        {!data && !error && (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading dossier…
          </div>
        )}

        {data && (
          <div className="p-5 space-y-5">
            {/* 2026-03-01 — Fraud flag banner (repeat-offender auto-suspend).
                Surfaces the count, reason, and pending appeal (if any) with
                approve/reject actions right in the modal. */}
            {data.user.fraud_flag && (
              <div className="border-2 border-red-500 bg-red-50 rounded-md p-4" data-testid="user-fraud-card">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-red-700 font-semibold">⚠ Fraud flag</p>
                    <p className="text-sm font-semibold text-red-900 mt-1">
                      Auto-suspended after {data.user.fraud_flag.count_30d} CAD-mismatch delists in the last 30 days
                    </p>
                    <p className="text-[11px] text-red-700 font-mono mt-1">
                      Flagged {data.user.fraud_flag.flagged_at ? new Date(data.user.fraud_flag.flagged_at).toLocaleString() : "—"}
                      {data.user.fraud_flag.previous_kyc_status ? ` · Previous KYC: ${data.user.fraud_flag.previous_kyc_status}` : ""}
                    </p>
                  </div>
                </div>
                {appeal ? (
                  <div className="mt-3 border border-red-300 bg-white rounded-md p-3 space-y-3" data-testid="appeal-review-card">
                    <div>
                      <p className="text-[11px] font-mono uppercase text-slate-500">Seller's appeal · {new Date(appeal.created_at).toLocaleString()}</p>
                      <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{appeal.message}</p>
                    </div>
                    <textarea
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      rows={2}
                      data-testid="appeal-reject-note"
                      placeholder="Optional note if rejecting (shown to seller)…"
                      className="w-full border border-border rounded-md p-2 text-xs bg-white"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolve("approve")}
                        disabled={resolving}
                        data-testid="appeal-approve"
                        className="bg-emerald-600 text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60"
                      >
                        Approve & reinstate
                      </button>
                      <button
                        onClick={() => resolve("reject")}
                        disabled={resolving}
                        data-testid="appeal-reject"
                        className="border border-red-500 text-red-700 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-red-50 disabled:opacity-60"
                      >
                        Reject appeal
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-red-700 italic mt-2">No pending appeal from this seller.</p>
                )}
              </div>
            )}
            {/* KYC status card — highlighted so status is the first thing
                admins see when reviewing a seller. */}
            {(data.user.role === "seller" || data.user.role === "both") && (
              <div className="border-2 border-primary/40 bg-primary/5 rounded-md p-4" data-testid="ud-kyc-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-mono uppercase tracking-widest text-primary font-semibold">KYC / Payout secrets</p>
                  <StatusTag status={data.user.kyc_status || "not_started"} />
                </div>
                {!twofaEnabled ? (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="ud-pan-need-2fa">
                    ⚠ You need to enable 2FA on your admin account before viewing sensitive KYC data. Go to <span className="font-semibold">Settings → Two-factor authentication</span> and complete the authenticator-app enrolment.
                  </p>
                ) : !decryptedPan ? (
                  showTotpPrompt ? (
                    <div className="space-y-2" data-testid="ud-pan-totp-form">
                      <p className="text-xs text-muted-foreground">Enter the 6-digit code from your authenticator app to view PAN + bank details:</p>
                      <div className="flex gap-2">
                        <input
                          inputMode="numeric"
                          maxLength={6}
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          className="border border-border rounded px-3 py-2 text-sm font-mono text-center tracking-widest w-32"
                          data-testid="ud-pan-totp-input"
                          placeholder="123 456"
                        />
                        <button
                          onClick={authorisePan}
                          disabled={totpBusy || totpCode.length !== 6}
                          data-testid="ud-pan-totp-submit"
                          className="bg-primary text-white rounded px-4 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                        >
                          {totpBusy ? "Verifying…" : "Authorise"}
                        </button>
                        <button
                          onClick={() => { setShowTotpPrompt(false); setTotpCode(""); }}
                          className="border border-border rounded px-3 py-2 text-xs hover:bg-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTotpPrompt(true)}
                      data-testid="ud-pan-reveal-btn"
                      className="border border-primary text-primary rounded px-4 py-2 text-xs font-semibold hover:bg-primary hover:text-white"
                    >
                      🔒 Reveal PAN + bank details (2FA)
                    </button>
                  )
                ) : (
                  <div className="space-y-2 text-sm" data-testid="ud-pan-details">
                    <div className="bg-white border border-border rounded p-3 space-y-1.5 font-mono text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground">PAN:</span> <span className="font-bold">{decryptedPan.pan || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Full name:</span> <span>{decryptedPan.full_name || "—"}</span></div>
                      {decryptedPan.bank_account_number && (
                        <>
                          <div className="flex justify-between"><span className="text-muted-foreground">Bank account:</span> <span className="font-bold">{decryptedPan.bank_account_number}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Bank holder:</span> <span>{decryptedPan.bank_registered_name || "—"}</span></div>
                        </>
                      )}
                    </div>
                    {panSession?.token && (
                      <button
                        onClick={openPanImage}
                        data-testid="ud-pan-image-btn"
                        className="bg-primary text-white rounded px-4 py-2 text-xs font-semibold hover:bg-primary/90"
                      >
                        📄 Open PAN card image (15-min link)
                      </button>
                    )}
                    <p className="text-[10px] text-muted-foreground italic">Every reveal + download is audit-logged. 5+ reveals per hour triggers a super-admin alert.</p>
                  </div>
                )}
              </div>
            )}

            {/* KYC status card — highlighted so status is the first thing */}
            <div className="border border-border rounded-md p-4 bg-secondary/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">KYC status</p>
                <StatusTag status={data.user.kyc_status === "none" ? "pending" : (data.user.kyc_status || "pending")} />
              </div>
              {data.user.kyc_document && (
                <p className="text-xs mt-1"><b>Document:</b> {data.user.kyc_document}</p>
              )}
              {data.user.kyc_notes && (
                <p className="text-xs mt-1"><b>Notes:</b> {data.user.kyc_notes}</p>
              )}
              {data.user.kyc_verified_at && (
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">Verified {new Date(data.user.kyc_verified_at).toLocaleString()}</p>
              )}
            </div>

            {/* Contact block */}
            <div className="border border-border rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Contact</p>
                {/* 2026-03-01 — Manual mobile verify for markets MSG91 can't route to */}
                {!data.user.phone_verified && (data.user.phone || data.user.mobile) && (
                  <button
                    onClick={() => setShowManualVerify(!showManualVerify)}
                    data-testid="manual-verify-mobile-toggle"
                    className="text-[11px] px-2 py-1 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 font-semibold"
                  >
                    Manually verify mobile
                  </button>
                )}
                {data.user.phone_verified && (
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-300" data-testid="mobile-verified-chip">
                    ✓ mobile verified{data.user.phone_verified_manually ? " (manual)" : ""}
                  </span>
                )}
              </div>
              {showManualVerify && (
                <div className="mb-3 border border-blue-200 bg-blue-50/60 rounded p-3 space-y-2" data-testid="manual-verify-form">
                  <p className="text-xs text-blue-900">Use this when the user's country isn't SMS-routable and you've confirmed their identity via WhatsApp/call. Every manual verify is audit-logged.</p>
                  <textarea
                    value={manualVerifyReason}
                    onChange={(e) => setManualVerifyReason(e.target.value)}
                    rows={2}
                    placeholder='e.g. "Confirmed identity via WhatsApp call on 2026-03-01, matched PAN on file"'
                    data-testid="manual-verify-reason"
                    className="w-full border border-border rounded p-2 text-xs bg-white"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={manualVerifyMobile}
                      disabled={manualVerifying || manualVerifyReason.trim().length < 6}
                      data-testid="manual-verify-submit"
                      className="bg-blue-700 text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-blue-800 disabled:opacity-50"
                    >
                      {manualVerifying ? "Verifying…" : "Mark verified"}
                    </button>
                    <button
                      onClick={() => { setShowManualVerify(false); setManualVerifyReason(""); }}
                      className="border border-border rounded px-3 py-1.5 text-xs hover:bg-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <DetailRow icon={<Mail className="h-3.5 w-3.5" />} label="Email" value={data.user.email} testid="ud-email" />
                <DetailRow icon={<Phone className="h-3.5 w-3.5" />} label="Mobile" value={data.user.phone || data.user.mobile || "—"} testid="ud-phone" />
                <DetailRow icon={<MapPin className="h-3.5 w-3.5" />} label="Address" value={data.user.address || data.user.city || "—"} testid="ud-address" />
                <DetailRow label="Business" value={data.user.business_name || data.user.company || "—"} testid="ud-business" />
                <DetailRow label="GSTIN" value={data.user.gstin || "—"} testid="ud-gstin" />
                <DetailRow label="PAN" value={data.user.pan || "—"} testid="ud-pan" />
              </div>
            </div>

            {/* Bank / payout info — only relevant for sellers */}
            {(data.user.role === "seller" || data.user.role === "both" || data.user.role === "cam_vendor") && (
              <div className="border border-border rounded-md p-4" data-testid="ud-payout-block">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Payout / Bank</p>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <DetailRow label="Bank name" value={data.payout?.bank_name || "—"} testid="ud-bank-name" />
                  <DetailRow label="Branch" value={data.payout?.bank_branch || "—"} testid="ud-bank-branch" />
                  <DetailRow label="Account holder" value={data.payout?.account_holder || "—"} testid="ud-bank-holder" />
                  <DetailRow label="Account #" value={data.payout?.account_masked || "—"} testid="ud-bank-acct" />
                  <DetailRow label={data.payout?.swift_code ? "SWIFT / BIC" : "IFSC"} value={data.payout?.swift_code || data.payout?.ifsc || "—"} testid="ud-bank-ifsc" />
                  <DetailRow label="UPI" value={data.payout?.upi_vpa || "—"} testid="ud-bank-upi" />
                  <DetailRow label="Bank verified" value={data.payout?.bank_verified_at ? new Date(data.payout.bank_verified_at).toLocaleString("en-IN") : (data.payout?.bank_needs_manual_review ? "Awaiting manual review" : "—")} testid="ud-bank-verified" />
                  <DetailRow label="PAN / Tax ID" value={data.payout?.pan_masked ? `${data.payout.pan_masked} (${data.payout.tax_id_type || "PAN"})` : (data.user.pan || "—")} testid="ud-bank-pan" />
                </div>
                <p className="text-[10px] text-muted-foreground mt-3">Full account number and PAN are encrypted at rest — use the KYC viewer (2FA) to reveal.</p>
              </div>
            )}

            {/* Activity + counts */}
            <div className="grid sm:grid-cols-4 gap-2">
              {Object.entries(data.stats || {}).map(([k, v]) => (
                <KPI key={k} label={k.replace(/_/g, " ")} value={v} accent={String(k).includes("pending") ? "text-amber-500" : ""} />
              ))}
            </div>

            {data.recent_listings?.length > 0 && (
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Recent listings</p>
                <div className="space-y-1">
                  {data.recent_listings.map((l) => (
                    <a key={l.id} href={`/design/${l.id}`} target="_blank" rel="noreferrer" className="block border border-border rounded-md px-3 py-1.5 text-xs hover:border-primary">
                      <span className="font-mono text-muted-foreground mr-2">{l.product_id || l.id.slice(0, 8)}</span>
                      {l.title}
                      <span className="ml-2 text-[10px] text-muted-foreground">· {l.status}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {data.recent_orders?.length > 0 && (
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Recent orders</p>
                <div className="space-y-1">
                  {data.recent_orders.map((o) => (
                    <div key={o.id} className="border border-border rounded-md px-3 py-1.5 text-xs">
                      <span className="font-mono text-muted-foreground mr-2">{o.id.slice(0, 8)}</span>
                      {o.listing_title || "(unknown listing)"}
                      <span className="ml-2 text-[10px] text-muted-foreground">· {inr(o.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {icon}{label}
      </span>
      <span className="mt-0.5 break-words">{value}</span>
    </div>
  );
}


function TransactionsView({ payoutAct }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const qs = new URLSearchParams();
    if (kind !== "all") qs.set("kind", kind);
    if (q) qs.set("q", q);
    if (dfrom) qs.set("date_from", dfrom);
    if (dto) qs.set("date_to", dto);
    setLoading(true);
    api.get(`/admin/transactions?${qs}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [kind, q, dfrom, dto]);
  const fmtIST = (iso) => {
    if (!iso) return { d: "—", t: "" };
    const d = new Date(iso);
    return {
      d: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }),
      t: d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }),
    };
  };
  const copyTxn = (id) => { navigator.clipboard.writeText(id); toast.success("Txn ID copied"); };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-display font-semibold">Daily transactions</p>
        <span className="text-xs text-muted-foreground">{rows.length} row{rows.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="tx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search txn id, design, seller, buyer, UTR…" className="border border-border rounded px-3 py-1.5 text-sm w-full md:w-80" />
        <div className="flex gap-1 text-xs">
          {["all", "sale", "payout", "refund"].map((k) => (
            <button key={k} data-testid={`tx-filter-${k}`} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded ${kind === k ? "bg-primary text-primary-foreground" : "border border-border"}`}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        <input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" />
        <input type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" />
      </div>
      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary/50 text-left">
            <tr>{["Type", "Txn ID", "Date · Time", "Design (code)", "Seller", "Buyer", "Amount", "Payout / UTR", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.map((r) => {
              const dt = fmtIST(r.when);
              return (
                <tr key={r.txn_id} data-testid={`tx-row-${r.txn_id}`} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-mono ${
                      r.type === "sale" ? "bg-emerald-50 text-emerald-700" :
                      r.type === "payout" ? "bg-blue-50 text-blue-700" :
                      "bg-red-50 text-red-700"
                    }`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2 font-mono">
                    <button data-testid={`tx-copy-${r.txn_id}`} onClick={() => copyTxn(r.txn_id)} title={r.txn_id} className="text-primary hover:underline text-[11px]">
                      {r.txn_id?.slice(0, 12)}…
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap text-[11px]">
                    <div>{dt.d}</div>
                    <div className="text-muted-foreground">{dt.t}</div>
                  </td>
                  <td className="px-3 py-2">
                    {r.type === "payout" ? (
                      <span className="text-muted-foreground italic">{r.listing_title}</span>
                    ) : (
                      <div>
                        <a href={`/listing/${r.listing_id}`} target="_blank" rel="noopener noreferrer" data-testid={`tx-listing-${r.txn_id}`} className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                          {r.listing_title || "Untitled"}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <div className="text-[10px] font-mono text-muted-foreground">{r.listing_code}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.party_seller_name || "—"}</div>
                    {r.party_seller_id && <div className="text-[10px] font-mono text-muted-foreground">{r.party_seller_id.slice(0, 8)}</div>}
                  </td>
                  <td className="px-3 py-2">
                    {r.party_buyer_name ? (
                      <>
                        <div className="font-medium">{r.party_buyer_name}</div>
                        {r.party_buyer_email && <div className="text-[10px] font-mono text-muted-foreground">{r.party_buyer_email}</div>}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{inr(r.amount)}</td>
                  <td className="px-3 py-2">
                    {r.type === "payout" ? (
                      <div>
                        <StatusTag status={r.status} />
                        {r.utr_reference && (
                          <div className="mt-1 font-mono text-[10px] flex items-center gap-1">
                            <span className="select-all">{r.utr_reference}</span>
                            <button onClick={() => { navigator.clipboard.writeText(r.utr_reference); toast.success("UTR copied"); }} className="text-primary underline">copy</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <StatusTag status={r.payout_status} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.type === "sale" && !r.released && (
                      <button onClick={() => payoutAct(r.txn_id, "release")} className="text-[10px] bg-primary text-primary-foreground rounded px-2 py-1">Release</button>
                    )}
                    {r.type === "sale" && r.released && (
                      <button onClick={() => payoutAct(r.txn_id, "hold")} className="text-[10px] border border-border rounded px-2 py-1">Hold</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-4 py-16 text-center text-muted-foreground">No transactions in this window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Finance() {
  const [tx, setTx] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payFilter, setPayFilter] = useState("open"); // open | history
  const [payModal, setPayModal] = useState(null); // { p, action }
  const [utr, setUtr] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [statementSeller, setStatementSeller] = useState(null); // {id, name}
  const [statement, setStatement] = useState(null);
  const [statementFrom, setStatementFrom] = useState("");
  const [statementTo, setStatementTo] = useState("");
  const [statementLoading, setStatementLoading] = useState(false);
  const load = () => {
    api.get("/admin/transactions").then((r) => setTx(r.data));
    api.get("/admin/payouts").then((r) => setPayouts(r.data));
    api.get("/admin/orders/duplicates").then((r) => setDupes(r.data)).catch(() => {});
    setReconLoading(true);
    api.get("/admin/reconciliation").then((r) => setRecon(r.data)).catch(() => {}).finally(() => setReconLoading(false));
  };
  useEffect(() => { load(); }, []);
  const runDedupe = async () => {
    const unsafe = dupes.filter(d => d.safe_to_merge === false).length;
    let msg = `Merge ${dupes.length - unsafe} duplicate order group(s)? The oldest order in each is kept; extras are marked "duplicate" and won't count in earnings anymore.`;
    if (unsafe > 0) msg += `\n\n${unsafe} unsafe group(s) with mismatched buyers will be SKIPPED for manual review.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/admin/orders/dedupe");
      toast.success(`Merged ${r.data.duplicates_merged} order(s)` + (r.data.skipped_unsafe ? ` · ${r.data.skipped_unsafe} skipped as unsafe` : ""));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openStatement = (seller_id, seller_name) => {
    setStatementSeller({ id: seller_id, name: seller_name });
    setStatement(null);
    setStatementFrom(""); setStatementTo("");
  };
  const loadStatement = async () => {
    if (!statementSeller) return;
    setStatementLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statementFrom) qs.set("date_from", statementFrom);
      if (statementTo) qs.set("date_to", statementTo);
      const r = await api.get(`/admin/sellers/${statementSeller.id}/statement?${qs.toString()}`);
      setStatement(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setStatementLoading(false); }
  };
  useEffect(() => { if (statementSeller) loadStatement(); /* eslint-disable-next-line */ }, [statementSeller, statementFrom, statementTo]);
  const downloadStatementCsv = async () => {
    if (!statementSeller) return;
    const qs = new URLSearchParams({ export: "csv" });
    if (statementFrom) qs.set("date_from", statementFrom);
    if (statementTo) qs.set("date_to", statementTo);
    try {
      const r = await api.get(`/admin/sellers/${statementSeller.id}/statement?${qs.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${(statementSeller.name || "seller").replace(/\s+/g, "_")}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  // Live receipt preview (PNG of page 1) — debounced to spare the server.
  useEffect(() => {
    if (!payModal || payModal.action !== "approve") return;
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post(
          `/admin/payouts/${payModal.p.id}/receipt-preview`,
          { utr_reference: utr.trim(), notes: notes.trim() },
          { responseType: "blob" },
        );
        if (cancelled) return;
        const blob = new Blob([r.data], { type: "image/png" });
        const nextUrl = URL.createObjectURL(blob);
        setPreviewUrl((prev) => {
          if (prev) setTimeout(() => URL.revokeObjectURL(prev), 2000);
          return nextUrl;
        });
      } catch (_e) { /* silent — modal still usable without preview */ }
      finally { if (!cancelled) setPreviewLoading(false); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [payModal, utr, notes]);
  const payoutAct = async (id, action) => { await api.post(`/admin/orders/${id}/payout?action=${action}`); toast.success("Updated"); load(); };
  const openModal = (p, action) => { setPayModal({ p, action }); setUtr(""); setNotes(""); setPreviewUrl(null); };
  const closeModal = () => { setPayModal(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };
  const submitProcess = async () => {
    if (!payModal) return;
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${payModal.p.id}/process`, {
        action: payModal.action,
        utr_reference: payModal.action === "approve" ? utr.trim() : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(payModal.action === "approve" ? "Payout marked paid" : "Payout rejected");
      setPayModal(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };
  const copy = (v) => { if (!v) return; navigator.clipboard.writeText(v); toast.success("Copied"); };
  const downloadReceipt = async (pid) => {
    try {
      const r = await api.get(`/admin/payouts/${pid}/receipt`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `payout_${pid.slice(0, 8)}_receipt.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const requestReverify = async (p) => {
    const reason = prompt(
      "Explain why the seller needs to re-add their bank account\n(they'll get this in the email + in-app notification):",
      "We only have the last 4 digits of your bank account on file, and need the full account number to complete this transfer.",
    );
    if (!reason) return;
    try {
      await api.post(`/admin/payouts/${p.id}/request-reverify`, { reason });
      toast.success("Seller notified — payout parked until they re-verify");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const open = payouts.filter(p => p.status === "requested");
  const history = payouts.filter(p => p.status !== "requested");
  const rows = payFilter === "open" ? open : history;

  // ---- Party Statement sub-tab state ----
  const [subTab, setSubTab] = useState("withdrawals"); // withdrawals | transactions | party
  const [sellerQuery, setSellerQuery] = useState("");
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [partyFrom, setPartyFrom] = useState("");
  const [partyTo, setPartyTo] = useState("");
  const [partyStmt, setPartyStmt] = useState(null);
  const [partyLoading, setPartyLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  useEffect(() => {
    if (subTab !== "party") return;
    api.get(`/admin/sellers?q=${encodeURIComponent(sellerQuery)}`).then((r) => setSellers(r.data)).catch(() => {});
  }, [subTab, sellerQuery]);
  useEffect(() => {
    if (subTab === "party") api.get("/admin/statements/last-run").then((r) => setLastRun(r.data)).catch(() => {});
  }, [subTab]);
  const loadPartyStmt = async () => {
    if (!selectedSeller) return;
    setPartyLoading(true);
    try {
      const qs = new URLSearchParams();
      if (partyFrom) qs.set("date_from", partyFrom);
      if (partyTo) qs.set("date_to", partyTo);
      const r = await api.get(`/admin/sellers/${selectedSeller.id}/statement?${qs.toString()}`);
      setPartyStmt(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPartyLoading(false); }
  };
  useEffect(() => { if (selectedSeller) loadPartyStmt(); /* eslint-disable-next-line */ }, [selectedSeller, partyFrom, partyTo]);
  // Backup status — loaded once for the Finance tab
  useEffect(() => {
    api.get("/admin/backup/history")
      .then((r) => setBackupInfo(r.data[0] || { none: true }))
      .catch(() => {});
  }, []);
  const runBackupNow = async () => {
    if (!window.confirm("Trigger a manual daily financial backup now? The encrypted JSON + CSVs will be emailed to hello@jeweltix.com.")) return;
    setBackupBusy(true);
    try {
      const r = await api.post("/admin/backup/run?backup_type=daily");
      toast.success(`Backup created · ${r.data.email_status}`);
      setBackupInfo(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBackupBusy(false); }
  };
  const downloadPartyPdf = async (mask) => {
    if (!selectedSeller) return;
    const qs = new URLSearchParams();
    if (partyFrom) qs.set("date_from", partyFrom);
    if (partyTo) qs.set("date_to", partyTo);
    if (mask) qs.set("mask_buyer", "true");
    try {
      const r = await api.get(`/admin/sellers/${selectedSeller.id}/statement.pdf?${qs.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${mask ? "seller" : "admin"}_${(selectedSeller.name || "").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const emailPartyStatement = async () => {
    if (!selectedSeller) return;
    if (!window.confirm(`Send this statement by email to ${selectedSeller.email}? Buyer identities will NOT be included.`)) return;
    const qs = new URLSearchParams();
    if (partyFrom) qs.set("date_from", partyFrom);
    if (partyTo) qs.set("date_to", partyTo);
    try {
      const r = await api.post(`/admin/sellers/${selectedSeller.id}/statement/email?${qs.toString()}`);
      toast.success(`Emailed to ${r.data.sent_to}`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const runMonthly = async () => {
    if (!window.confirm("Run the monthly statement mailer NOW for the previous calendar month? This emails every seller with activity.")) return;
    try {
      const r = await api.post("/admin/statements/run-monthly");
      toast.success(`Sent ${r.data.sent} / skipped ${r.data.skipped} for ${r.data.label}`);
      api.get("/admin/statements/last-run").then((r) => setLastRun(r.data)).catch(() => {});
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tab nav */}
      <div className="flex gap-1 border-b border-border">
        {[
          ["withdrawals", "Withdrawals"],
          ["transactions", "Transactions"],
          ["party", "Party Statement"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`fin-subtab-${k}`}
            onClick={() => setSubTab(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              subTab === k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >{label}</button>
        ))}
      </div>

      {/* Backup + drift health strip — always visible in Finance */}
      <div data-testid="fin-health" className="border border-border rounded-md bg-secondary/30 px-3 py-2 flex flex-wrap items-center gap-3 text-xs">
        <ShieldCheck className={`w-4 h-4 ${backupInfo && !backupInfo.none ? "text-emerald-600" : "text-amber-600"}`} />
        <span>
          <b>Last financial backup:</b>{" "}
          {backupInfo?.backup_date ? new Date(backupInfo.backup_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST" : "never — please run one now"}
        </span>
        {backupInfo?.checksum && (
          <span className="font-mono text-[10px] text-muted-foreground">SHA {backupInfo.checksum.slice(0, 12)}…</span>
        )}
        {backupInfo?.drift_count > 0 && (
          <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 text-[10px] uppercase font-mono">Drift on {backupInfo.drift_count} seller(s)</span>
        )}
        <button data-testid="run-backup-now" onClick={runBackupNow} disabled={backupBusy}
          className="ml-auto text-xs bg-primary/10 text-primary rounded px-3 py-1.5 hover:bg-primary/20 disabled:opacity-50 inline-flex items-center gap-1">
          <Download className="w-3 h-3" /> {backupBusy ? "Running…" : "Run backup now"}
        </button>
      </div>

      {subTab === "withdrawals" && <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-display font-semibold">Withdrawal requests</p>
          <div className="flex gap-1 text-xs">
            <button data-testid="pay-filter-open" onClick={() => setPayFilter("open")} className={`px-3 py-1.5 rounded ${payFilter === "open" ? "bg-primary text-primary-foreground" : "border border-border"}`}>Open ({open.length})</button>
            <button data-testid="pay-filter-history" onClick={() => setPayFilter("history")} className={`px-3 py-1.5 rounded ${payFilter === "history" ? "bg-primary text-primary-foreground" : "border border-border"}`}>History ({history.length})</button>
          </div>
        </div>
        <div className="space-y-3">
          {rows.map((p) => (
            <div key={p.id} data-testid={`payout-row-${p.id}`} className="border border-border rounded-md bg-card p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid={`open-statement-${p.seller_id}`} onClick={() => openStatement(p.seller_id, p.seller_name)} className="font-medium hover:text-primary hover:underline">
                      {p.seller_name}
                    </button>
                    <StatusTag status={p.status} />
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{p.method}</span>
                    {p.seller?.kyc_status && <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">KYC {p.seller.kyc_status}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {p.seller?.email || "—"}{p.seller?.phone ? ` · ${p.seller.phone}` : ""}
                    {p.seller?.account_type ? ` · ${p.seller.account_type}` : ""}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">Requested {new Date(p.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg">{inr(p.amount)}</div>
                  {p.tds_amount > 0 && (
                    <div className="text-[11px] text-muted-foreground font-mono">
                      TDS {inr(p.tds_amount)} · Net {inr(p.net_amount)}
                    </div>
                  )}
                </div>
              </div>

              {/* KYC + destination */}
              <div className="mt-3 grid md:grid-cols-2 gap-3 text-xs">
                <div className="border border-border/70 rounded p-3 bg-secondary/30">
                  <p className="text-[10px] uppercase font-mono text-muted-foreground mb-1.5">Legal / PAN</p>
                  <div className="space-y-1">
                    <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{p.seller?.full_name || "—"}</span></div>
                    <div className="flex items-center gap-1"><span className="text-muted-foreground">PAN:</span> <span className="font-mono">{p.seller?.pan || "—"}</span>{p.seller?.pan && <button onClick={() => copy(p.seller.pan)} className="text-primary underline text-[10px]">copy</button>}</div>
                    {p.seller?.business_name && <div><span className="text-muted-foreground">Business:</span> {p.seller.business_name}</div>}
                    {p.seller?.gstin && <div className="flex items-center gap-1"><span className="text-muted-foreground">GSTIN:</span> <span className="font-mono">{p.seller.gstin}</span><button onClick={() => copy(p.seller.gstin)} className="text-primary underline text-[10px]">copy</button></div>}
                  </div>
                </div>
                <div className="border border-border/70 rounded p-3 bg-secondary/30">
                  <p className="text-[10px] uppercase font-mono text-muted-foreground mb-1.5">Transfer to</p>
                  {p.method === "bank" ? (
                    <div className="space-y-1">
                      <div><span className="text-muted-foreground">Holder:</span> <span className="font-medium">{p.details?.account_holder_name || "—"}</span></div>
                      <div><span className="text-muted-foreground">Bank:</span> <span className="font-medium" data-testid={`pay-bank-name-${p.id}`}>{p.details?.bank_name || "—"}{p.details?.bank_branch ? ` — ${p.details.bank_branch}` : ""}</span></div>
                      <div className="flex items-center gap-1"><span className="text-muted-foreground">A/c #:</span> <span className="font-mono select-all">{p.details?.account_number || (p.details?.account_last4 ? `XXXX${p.details.account_last4}` : "—")}</span>{p.details?.account_number && <button onClick={() => copy(p.details.account_number)} className="text-primary underline text-[10px]">copy</button>}</div>
                      <div className="flex items-center gap-1"><span className="text-muted-foreground">IFSC:</span> <span className="font-mono">{p.details?.ifsc || "—"}</span>{p.details?.ifsc && <button onClick={() => copy(p.details.ifsc)} className="text-primary underline text-[10px]">copy</button>}</div>
                      {!p.details?.account_number && p.details?.account_last4 && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-[10px] text-amber-700">Legacy record — only last-4 stored. Ask seller to re-verify.</span>
                          {p.status === "requested" && (
                            <button data-testid={`pay-reverify-${p.id}`} onClick={() => requestReverify(p)} className="text-[10px] bg-amber-500 text-white rounded px-2 py-0.5 hover:bg-amber-600">Request re-verify</button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div><span className="text-muted-foreground">Holder:</span> <span className="font-medium">{p.details?.registered_name || "—"}</span></div>
                      <div className="flex items-center gap-1"><span className="text-muted-foreground">UPI:</span> <span className="font-mono select-all">{p.details?.vpa || "—"}</span>{p.details?.vpa && <button onClick={() => copy(p.details.vpa)} className="text-primary underline text-[10px]">copy</button>}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Audit trail (for processed rows) */}
              {p.status !== "requested" && (
                <div className="mt-3 border-t border-border pt-3 text-xs grid md:grid-cols-3 gap-2">
                  <div><span className="text-muted-foreground">Processed by:</span> <span className="font-medium">{p.processed_by || p.reverify_requested_by_email || "—"}</span>{p.processed_by_email && <div className="text-[10px] font-mono text-muted-foreground">{p.processed_by_email}</div>}</div>
                  <div><span className="text-muted-foreground">Paid on:</span> <span className="font-mono">{p.paid_at ? new Date(p.paid_at).toLocaleString() : (p.processed_at ? new Date(p.processed_at).toLocaleString() : (p.reverify_requested_at ? new Date(p.reverify_requested_at).toLocaleString() : "—"))}</span></div>
                  <div><span className="text-muted-foreground">UTR:</span> <span className="font-mono select-all">{p.utr_reference || "—"}</span>{p.utr_reference && <button onClick={() => copy(p.utr_reference)} className="ml-1 text-primary underline text-[10px]">copy</button>}</div>
                  {p.payment_notes && <div className="md:col-span-3 text-[11px] text-muted-foreground"><span className="font-mono">Notes:</span> {p.payment_notes}</div>}
                  {p.reverify_reason && <div className="md:col-span-3 text-[11px] text-amber-700"><span className="font-mono">Re-verify sent:</span> {p.reverify_reason}</div>}
                  {p.status === "paid" && (
                    <div className="md:col-span-3 mt-1">
                      <button data-testid={`pay-receipt-${p.id}`} onClick={() => downloadReceipt(p.id)} className="text-xs bg-primary/10 text-primary rounded px-2.5 py-1 hover:bg-primary/20 inline-flex items-center gap-1">
                        <Download className="w-3 h-3" /> Download signed PDF receipt {p.receipt_id ? `(${p.receipt_id})` : ""}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {p.status === "requested" && (
                <div className="mt-3 flex gap-2 justify-end">
                  <button data-testid={`pay-reject-${p.id}`} onClick={() => openModal(p, "reject")} className="text-xs border border-border rounded px-3 py-1.5">Reject</button>
                  <button data-testid={`pay-approve-${p.id}`} onClick={() => openModal(p, "approve")} className="text-xs bg-emerald-600 text-white rounded px-3 py-1.5">Mark paid (record UTR)</button>
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && <div className="border border-dashed border-border rounded-md p-10 text-center text-muted-foreground text-sm">No {payFilter === "open" ? "open" : "past"} payout requests.</div>}
        </div>
      </div>

      {/* Approval / rejection modal */}
      <Dialog open={!!payModal} onOpenChange={(v) => !v && closeModal()}>
        <DialogContent className={payModal?.action === "approve" ? "sm:max-w-5xl" : "sm:max-w-lg"}>
          <DialogHeader>
            <DialogTitle>{payModal?.action === "approve" ? "Record bank transfer" : "Reject payout"}</DialogTitle>
          </DialogHeader>
          {payModal && (
            <div className={payModal.action === "approve" ? "grid md:grid-cols-2 gap-5" : ""}>
              <div className="space-y-4 text-sm">
                <div className="border border-border rounded p-3 bg-secondary/40 text-xs space-y-1">
                  <div><span className="text-muted-foreground">Seller:</span> <span className="font-medium">{payModal.p.seller_name}</span></div>
                  <div><span className="text-muted-foreground">Amount to transfer (net):</span> <span className="font-mono font-medium">{inr(payModal.p.net_amount || payModal.p.amount)}</span></div>
                  <div><span className="text-muted-foreground">Destination:</span> <span className="font-mono">{payModal.p.method === "bank" ? `${payModal.p.details?.account_number || (payModal.p.details?.account_last4 ? `XXXX${payModal.p.details.account_last4}` : "—")} / ${payModal.p.details?.ifsc || "—"}` : (payModal.p.details?.vpa || "—")}</span></div>
                  {payModal.action === "approve" && (
                    <div className="mt-1.5 pt-1.5 border-t border-border/60 text-[11px] text-muted-foreground">
                      Transfer from <b>CADTIX</b> current account. Seller will see <b>CADTIX</b> on their bank statement (they've been briefed in the paid-email).
                    </div>
                  )}
                </div>
                {payModal.action === "approve" && (
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Bank UTR / Reference *</label>
                    <input data-testid="pay-utr-input" autoFocus value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. AXISN12345678 or NEFT UTR" className="mt-1 w-full border border-border rounded px-3 py-2 text-sm font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Copy this from your bank's transfer confirmation. Required — stored on the payout record for audit.</p>
                  </div>
                )}
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Notes (optional)</label>
                  <textarea data-testid="pay-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border border-border rounded px-3 py-2 text-sm" placeholder={payModal.action === "reject" ? "Why is this being rejected?" : "Any additional context (branch, batch id, etc.)"} />
                </div>
              </div>
              {payModal.action === "approve" && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Live receipt preview</label>
                    {previewLoading && <span className="text-[10px] text-muted-foreground animate-pulse">refreshing…</span>}
                  </div>
                  <div className="border border-border rounded bg-secondary/20 h-[520px] overflow-auto relative">
                    {previewUrl ? (
                      <img
                        data-testid="pay-receipt-preview"
                        alt="Receipt preview"
                        src={previewUrl}
                        className="w-full h-auto"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                        {previewLoading ? "Rendering preview…" : "Type a UTR to see the receipt update"}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">This is exactly what the seller will receive as a signed PDF attachment. UTR shows <span className="font-mono">— PENDING —</span> until you type one above.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={closeModal} className="text-xs border border-border rounded px-3 py-1.5">Cancel</button>
            <button
              data-testid="pay-modal-confirm"
              onClick={submitProcess}
              disabled={busy || (payModal?.action === "approve" && utr.trim().length < 4)}
              className={`text-xs rounded px-3 py-1.5 text-white ${payModal?.action === "approve" ? "bg-emerald-600" : "bg-red-600"} disabled:opacity-50`}
            >{busy ? "Saving…" : (payModal?.action === "approve" ? "Confirm paid" : "Confirm reject")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>}

      {subTab === "transactions" && (
      <TransactionsView payoutAct={payoutAct} />
      )}

      {subTab === "party" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-display font-semibold">Party Statement</p>
              <p className="text-xs text-muted-foreground">Pick any party — buyer, seller, or both — to see their full ledger. Buyer identities are visible to admins here but stripped from any statement emailed to a seller.</p>
            </div>
            <div className="flex items-center gap-3">
              {lastRun && lastRun.run_at && (
                <span className="text-[11px] text-muted-foreground">
                  Last auto-mailer run: <b>{lastRun.window?.label}</b> · sent {lastRun.sent} · skipped {lastRun.skipped}
                </span>
              )}
              <button data-testid="run-monthly" onClick={runMonthly} className="text-xs bg-primary/10 text-primary rounded px-3 py-1.5 hover:bg-primary/20 inline-flex items-center gap-1">
                <Zap className="w-3 h-3" /> Run monthly mailer now
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-[280px,1fr] gap-5">
            {/* Seller picker */}
            <div className="border border-border rounded-md bg-card p-3">
              <input
                data-testid="party-seller-search"
                type="text"
                value={sellerQuery}
                onChange={(e) => setSellerQuery(e.target.value)}
                placeholder="Search sellers…"
                className="w-full border border-border rounded px-2 py-1.5 text-sm mb-2"
              />
              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {sellers.map((s) => {
                  const badge = s.role === "both" ? { txt: "BUYER+SELLER", cls: "bg-amber-100 text-amber-700" }
                    : s.role === "buyer" ? { txt: "BUYER", cls: "bg-blue-100 text-blue-700" }
                    : s.role === "seller" ? { txt: "SELLER", cls: "bg-emerald-100 text-emerald-700" }
                    : null;
                  return (
                    <button
                      key={s.id}
                      data-testid={`party-seller-${s.id}`}
                      onClick={() => setSelectedSeller(s)}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs ${selectedSeller?.id === s.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
                    >
                      <div className="font-medium flex items-center gap-1.5">
                        {s.name}
                        {badge && <span className={`text-[9px] font-mono px-1 py-0.5 rounded uppercase ${selectedSeller?.id === s.id ? "bg-white/25 text-white" : badge.cls}`}>{badge.txt}</span>}
                      </div>
                      <div className={`text-[10px] font-mono ${selectedSeller?.id === s.id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{s.email}</div>
                    </button>
                  );
                })}
                {sellers.length === 0 && <p className="text-xs text-muted-foreground p-2">No sellers found.</p>}
              </div>
            </div>

            {/* Statement view */}
            <div className="border border-border rounded-md bg-card p-4 min-h-[300px]">
              {!selectedSeller && <p className="text-sm text-muted-foreground text-center py-16">Select a seller from the left to view their statement.</p>}
              {selectedSeller && (
                <>
                  <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-border">
                    <div>
                      <p className="text-sm font-semibold">{selectedSeller.name}</p>
                      <p className="text-[11px] font-mono text-muted-foreground">{selectedSeller.email}</p>
                    </div>
                    <div className="ml-auto flex items-end gap-2 flex-wrap">
                      <div>
                        <label className="block text-[10px] uppercase font-mono tracking-widest text-muted-foreground">From</label>
                        <input data-testid="party-from" type="date" value={partyFrom} onChange={(e) => setPartyFrom(e.target.value)} className="mt-1 border border-border rounded px-2 py-1 text-sm" />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-mono tracking-widest text-muted-foreground">To</label>
                        <input data-testid="party-to" type="date" value={partyTo} onChange={(e) => setPartyTo(e.target.value)} className="mt-1 border border-border rounded px-2 py-1 text-sm" />
                      </div>
                      <button onClick={() => { setPartyFrom(""); setPartyTo(""); }} className="text-xs border border-border rounded px-3 py-1.5">Clear</button>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap py-3 border-b border-border">
                    <button data-testid="party-pdf-admin" onClick={() => downloadPartyPdf(false)} className="text-xs bg-primary/10 text-primary rounded px-3 py-1.5 inline-flex items-center gap-1 hover:bg-primary/20">
                      <Download className="w-3 h-3" /> Admin PDF (with buyers)
                    </button>
                    <button data-testid="party-pdf-seller" onClick={() => downloadPartyPdf(true)} className="text-xs border border-border rounded px-3 py-1.5 inline-flex items-center gap-1">
                      <Download className="w-3 h-3" /> Seller PDF (no buyers)
                    </button>
                    <button data-testid="party-email" onClick={emailPartyStatement} className="text-xs bg-emerald-600 text-white rounded px-3 py-1.5 inline-flex items-center gap-1 hover:bg-emerald-700">
                      <Mail className="w-3 h-3" /> Send by email
                    </button>
                  </div>

                  {partyLoading && <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}
                  {!partyLoading && partyStmt && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 py-4">
                        {(partyStmt.summary.party_type === "buyer" ? [
                          ["Purchases", partyStmt.summary.purchases_count, ""],
                          ["Total spent", inr(partyStmt.summary.total_purchased), "text-red-600"],
                          ["Refunds received", inr(partyStmt.summary.total_refunds_received), "text-emerald-600"],
                          ["Net spend", inr(partyStmt.summary.net_spend), "font-semibold"],
                          ["Account type", "Buyer", "text-blue-600 font-semibold"],
                        ] : [
                          ["Sales", partyStmt.summary.sales_count, ""],
                          ["Total earned", inr(partyStmt.summary.total_sales_earned), "text-emerald-600"],
                          ["Refunded", inr(partyStmt.summary.total_refunds), "text-red-600"],
                          ["Paid out", inr(partyStmt.summary.total_paid_out), ""],
                          ["Current balance", inr(partyStmt.summary.current_balance), "font-semibold"],
                        ]).map(([label, val, cls]) => (
                          <div key={label} className="border border-border rounded p-2.5 bg-secondary/30">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
                            <p className={`mt-0.5 text-sm font-mono ${cls}`}>{val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="overflow-auto border border-border rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-secondary/50 sticky top-0 z-10">
                            <tr>
                              {["Date", "Kind", "Description", "Buyer (admin only)", "Gross", "Amount", "UTR", "Balance"].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase font-mono text-muted-foreground">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {partyStmt.ledger.map((r, i) => (
                              <tr key={i} className="border-t border-border">
                                <td className="px-3 py-1.5 font-mono text-[11px]">{(r.when || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-mono ${
                                  r.kind === "sale" ? "bg-emerald-50 text-emerald-700" :
                                  r.kind === "refund" ? "bg-red-50 text-red-700" :
                                  r.kind === "payout_paid" ? "bg-blue-50 text-blue-700" :
                                  r.kind === "purchase" ? "bg-indigo-50 text-indigo-700" :
                                  r.kind === "refund_received" ? "bg-teal-50 text-teal-700" :
                                  "bg-secondary text-muted-foreground"
                                }`}>{r.kind}</span></td>
                                <td className="px-3 py-1.5">{r.label}</td>
                                <td className="px-3 py-1.5 text-muted-foreground italic">{r.buyer || "—"}</td>
                                <td className="px-3 py-1.5 font-mono">{r.gross ? inr(r.gross) : "—"}</td>
                                <td className={`px-3 py-1.5 font-mono ${r.amount > 0 ? "text-emerald-600" : r.amount < 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.amount ? (r.amount > 0 ? "+" : "") + inr(r.amount) : "—"}</td>
                                <td className="px-3 py-1.5 font-mono text-[10px]">{r.utr || "—"}</td>
                                <td className="px-3 py-1.5 font-mono font-semibold">{inr(r.running_balance)}</td>
                              </tr>
                            ))}
                            {partyStmt.ledger.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No activity in this window.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Seller statement drawer */}
      <Dialog open={!!statementSeller} onOpenChange={(v) => !v && setStatementSeller(null)}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Statement — {statementSeller?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-border">
            <div>
              <label className="block text-[10px] uppercase font-mono tracking-widest text-muted-foreground">From</label>
              <input data-testid="stmt-from" type="date" value={statementFrom} onChange={(e) => setStatementFrom(e.target.value)} className="mt-1 border border-border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-mono tracking-widest text-muted-foreground">To</label>
              <input data-testid="stmt-to" type="date" value={statementTo} onChange={(e) => setStatementTo(e.target.value)} className="mt-1 border border-border rounded px-2 py-1 text-sm" />
            </div>
            <button onClick={() => { setStatementFrom(""); setStatementTo(""); }} className="text-xs border border-border rounded px-3 py-1.5">Clear</button>
            <div className="ml-auto">
              <button data-testid="stmt-csv" onClick={downloadStatementCsv} className="text-xs bg-primary/10 text-primary rounded px-3 py-1.5 inline-flex items-center gap-1 hover:bg-primary/20">
                <Download className="w-3 h-3" /> Download CSV
              </button>
            </div>
          </div>

          {statementLoading && <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!statementLoading && statement && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 py-3">
                {[
                  ["Sales", statement.summary.sales_count, ""],
                  ["Total earned", inr(statement.summary.total_sales_earned), "text-emerald-600"],
                  ["Refunded", inr(statement.summary.total_refunds), "text-red-600"],
                  ["Paid out", inr(statement.summary.total_paid_out), ""],
                  ["Current balance", inr(statement.summary.current_balance), "font-semibold"],
                ].map(([label, val, cls]) => (
                  <div key={label} className="border border-border rounded p-2.5 bg-secondary/30">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
                    <p className={`mt-0.5 text-sm font-mono ${cls}`}>{val}</p>
                  </div>
                ))}
              </div>
              {/* Ledger */}
              <div className="flex-1 overflow-auto border border-border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50 sticky top-0 z-10">
                    <tr>
                      {["Date", "Kind", "Description", "Buyer", "Gross", "Commission", "Amount", "UTR", "Balance"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] uppercase font-mono text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {statement.ledger.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-1.5 font-mono text-[11px]">{(r.when || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-mono ${
                          r.kind === "sale" ? "bg-emerald-50 text-emerald-700" :
                          r.kind === "refund" ? "bg-red-50 text-red-700" :
                          r.kind === "payout_paid" ? "bg-blue-50 text-blue-700" :
                          "bg-secondary text-muted-foreground"
                        }`}>{r.kind}</span></td>
                        <td className="px-3 py-1.5">{r.label}{r.receipt_id && <div className="text-[10px] font-mono text-muted-foreground">{r.receipt_id}</div>}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.buyer || "—"}</td>
                        <td className="px-3 py-1.5 font-mono">{r.gross ? inr(r.gross) : "—"}</td>
                        <td className="px-3 py-1.5 font-mono text-muted-foreground">{r.commission ? inr(r.commission) : "—"}</td>
                        <td className={`px-3 py-1.5 font-mono ${r.amount > 0 ? "text-emerald-600" : r.amount < 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.amount ? (r.amount > 0 ? "+" : "") + inr(r.amount) : "—"}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">{r.utr || "—"}</td>
                        <td className="px-3 py-1.5 font-mono font-semibold">{inr(r.running_balance)}</td>
                      </tr>
                    ))}
                    {statement.ledger.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No activity in this window.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const load = () => { api.get("/admin/disputes").then((r) => setDisputes(r.data)); api.get("/admin/employees").then((r) => setEmployees(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      {disputes.map((d) => <DisputeCard key={d.id} d={d} employees={employees} onDone={load} />)}
      {disputes.length === 0 && <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground">No disputes.</div>}
    </div>
  );
}

function DisputeCard({ d, employees, onDone }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [action, setAction] = useState("release");
  const [partial, setPartial] = useState("");
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState(d);
  const openTicket = async () => { setOpen(true); const { data } = await api.get(`/disputes/${d.id}`); setDetail(data); };
  const send = async () => { await api.post(`/disputes/${d.id}/message`, { message: msg }); setMsg(""); const { data } = await api.get(`/disputes/${d.id}`); setDetail(data); };
  const assign = async (eid) => { await api.post(`/admin/disputes/${d.id}/assign?employee_id=${eid}`); toast.success("Assigned"); onDone(); };
  const resolve = async () => {
    try { await api.post(`/admin/disputes/${d.id}/resolve`, { action, partial_amount: partial ? Number(partial) : null, note }); toast.success("Dispute resolved"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="border border-border rounded-md bg-card p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-medium">{d.listing_title}</p>
          <p className="font-mono text-xs text-muted-foreground">{d.issue_type} · buyer {d.buyer_name} · {new Date(d.created_at).toLocaleDateString()}{d.assigned_name ? ` · @${d.assigned_name}` : ""}</p>
        </div>
        <StatusTag status={d.status} />
        {employees.length > 0 && d.status !== "resolved" && (
          <select onChange={(e) => e.target.value && assign(e.target.value)} defaultValue="" className="text-xs border border-border rounded-md p-1.5 bg-background">
            <option value="">Assign…</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
        <Dialog open={open} onOpenChange={(o) => o ? openTicket() : setOpen(false)}>
          <DialogTrigger asChild><button data-testid={`open-dispute-${d.id}`} className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm">Open</button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Ticket · {d.listing_title}</DialogTitle></DialogHeader>
            <div className="max-h-52 overflow-auto space-y-2 border border-border rounded-md p-3 bg-background">
              {(detail.messages || []).map((m, i) => (
                <div key={m.id || m.created_at || `msg-${i}`} className={`text-sm ${m.sender === "staff" ? "text-primary" : ""}`}><span className="font-mono text-[10px] uppercase mr-2">{m.sender}</span>{m.message}</div>
              ))}
            </div>
            <div className="flex gap-2"><input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply…" className="flex-1 border border-border rounded-md p-2 text-sm bg-background" /><button onClick={send} className="bg-secondary rounded-md px-3 text-sm">Send</button></div>
            {d.status !== "resolved" && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-sm font-medium">Resolve</p>
                <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="resolve-action" className="w-full border border-border rounded-md p-2 text-sm bg-background">
                  <option value="release">Release payment to seller</option>
                  <option value="refund_full">Refund buyer fully</option>
                  <option value="partial">Partial refund</option>
                  <option value="replace">Replace file</option>
                </select>
                {action === "partial" && <input value={partial} onChange={(e) => setPartial(e.target.value)} placeholder="Refund amount (INR)" className="w-full border border-border rounded-md p-2 text-sm bg-background" />}
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note" className="w-full border border-border rounded-md p-2 text-sm bg-background" />
                <DialogFooter><button onClick={resolve} data-testid="resolve-dispute" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium">Resolve & execute</button></DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragIdx = useRef(null);
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/categories", { name, order: cats.length });
    setName(""); toast.success("Category added"); load();
  };
  const del = async (id) => { await api.delete(`/categories/${id}`); load(); };

  // Drag & drop reorder — only top-level parents shown; sub-categories move
  // implicitly with their parent because backend groups by parent_id at read.
  const parents = cats.filter((c) => !c.parent_id);

  const onDragStart = (i) => (e) => {
    dragIdx.current = i;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setCats((prev) => {
      const p = [...prev];
      const parentsList = p.filter((c) => !c.parent_id);
      const [moved] = parentsList.splice(from, 1);
      parentsList.splice(i, 0, moved);
      // Rebuild the whole `cats` array preserving sub-cats' relative order
      const subs = p.filter((c) => c.parent_id);
      dragIdx.current = i;
      setDirty(true);
      return [...parentsList, ...subs];
    });
  };
  const onDragEnd = () => { dragIdx.current = null; };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const ordered_ids = parents.map((c) => c.id);
      await api.post("/admin/categories/reorder", { ordered_ids });
      toast.success("Homepage order saved.");
      setDirty(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="categories-tab">
      <form onSubmit={add} className="flex gap-2 mb-6">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="new-category" className="flex-1 border border-border rounded-md p-2.5 text-sm bg-card" />
        <button className="bg-primary text-primary-foreground rounded-md px-4 text-sm font-medium">Add</button>
      </form>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Drag rows to reorder — this is exactly the order buyers see on the homepage strip and marketplace filter.</p>
        {dirty && (
          <button
            onClick={saveOrder}
            disabled={saving}
            data-testid="save-category-order"
            className="ml-3 bg-primary text-primary-foreground rounded-md px-4 py-1.5 text-xs font-semibold disabled:opacity-60"
          >{saving ? "Saving…" : "Save order"}</button>
        )}
      </div>
      <div className="space-y-2">
        {parents.map((c, i) => (
          <div
            key={c.id}
            draggable
            onDragStart={onDragStart(i)}
            onDragOver={onDragOver(i)}
            onDragEnd={onDragEnd}
            data-testid={`cat-row-${c.id}`}
            className="border border-border rounded-md bg-card p-3 cursor-move hover:border-primary/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-mono text-[10px] tracking-widest bg-secondary text-muted-foreground px-2 py-0.5 rounded">#{i + 1}</span>
                <span className="font-medium">{c.name}</span>
              </div>
              <button onClick={() => del(c.id)} data-testid={`cat-del-${c.id}`} className="text-xs text-red-500 hover:text-red-600">Delete</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2 ml-6">{cats.filter((s) => s.parent_id === c.id).map((s) => <span key={s.id} className="font-mono text-[11px] bg-secondary px-2 py-0.5 rounded">{s.name}</span>)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", permissions: [] });
  const load = () => api.get("/admin/employees").then((r) => setEmployees(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/employees", f); toast.success("Employee created"); setF({ name: "", email: "", password: "", permissions: [] }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/employees/${id}`); load(); };
  const togglePerm = (p) => setF((s) => ({ ...s, permissions: s.permissions.includes(p) ? s.permissions.filter((x) => x !== p) : [...s.permissions, p] }));
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="border border-border rounded-md bg-card p-6 space-y-3 h-fit">
        <h2 className="font-display font-semibold">Create employee</h2>
        <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" className="w-full border border-border rounded-md p-2.5 text-sm bg-background" />
        <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="emp-email" className="w-full border border-border rounded-md p-2.5 text-sm bg-background" />
        <input placeholder="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="emp-password" className="w-full border border-border rounded-md p-2.5 text-sm bg-background" />
        <div className="space-y-1.5">{PERMS.map((p) => <label key={p.key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} /> {p.label}</label>)}</div>
        <button onClick={create} data-testid="create-employee" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold w-full">Create employee</button>
      </div>
      <div className="space-y-2">
        {employees.map((e) => (
          <div key={e.id} className="border border-border rounded-md bg-card p-4">
            <div className="flex items-center justify-between"><div><p className="font-medium">{e.name}</p><p className="font-mono text-xs text-muted-foreground">{e.email}</p></div><button onClick={() => del(e.id)} className="text-xs text-red-500">Remove</button></div>
            <div className="flex flex-wrap gap-1.5 mt-2">{(e.permissions || []).map((p) => <span key={p} className="font-mono text-[10px] uppercase bg-primary/10 text-primary px-2 py-0.5 rounded">{p}</span>)}</div>
          </div>
        ))}
        {employees.length === 0 && <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground">No employees yet.</div>}
      </div>
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="border border-border rounded-md bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left"><tr>{["Actor", "Action", "Detail", "Time"].map((h) => <th key={h} className="px-4 py-2.5 text-[11px] uppercase font-mono text-muted-foreground">{h}</th>)}</tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-border">
              <td className="px-4 py-2.5">{l.actor_name}</td><td className="px-4 py-2.5 font-mono text-xs text-primary">{l.action}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.detail}</td><td className="px-4 py-2.5 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No activity yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Forensics() {
  const [file, setFile] = useState(null);
  const [listingId, setListingId] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // debounced listing search
  useEffect(() => {
    if (q.length < 2) { setSuggest([]); return; }
    const t = setTimeout(() => {
      api.get("/search/suggest", { params: { q } })
        .then((r) => setSuggest(r.data?.listings || []))
        .catch(() => setSuggest([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const pickListing = (l) => {
    setListingId(l.id);
    setQ(l.title);
    setSuggest([]);
  };

  const submit = async () => {
    if (!file) return toast.error("Drop a suspected STL file first");
    if (!listingId) return toast.error("Pick the listing this leak came from");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("listing_id", listingId);
      fd.append("leaked_file", file);
      const r = await api.post("/admin/trace-leak", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const best = r.data?.matches?.[0];
      if (best?.signal > 0.5) toast.success(`Match found — ${best.email || best.subject}`);
      else if (r.data?.matches?.length) toast.info("Weak matches only — inspect table");
      else toast.warning(r.data?.note || "No conclusive match");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setListingId(""); setQ(""); setResult(null); };

  return (
    <div className="space-y-6" data-testid="forensics-tab">
      <div className="border border-border rounded-md bg-card p-6 space-y-5">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Fingerprint className="h-5 w-5 text-primary" /> Leak Forensics</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
            Drop a suspected leaked STL. We compare it to the listing's clean baseline and rank every known buyer + recent previewer by watermark residual. Lowest score / highest signal = strongest match.
          </p>
        </div>

        {/* Listing picker */}
        <div className="relative">
          <label className="text-xs font-mono uppercase text-muted-foreground">Listing</label>
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setListingId(""); }}
            placeholder="Search by title, or paste a listing UUID directly…"
            data-testid="forensics-listing-input"
            className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono"
          />
          {listingId && (
            <div className="mt-1 text-[11px] font-mono text-emerald-500" data-testid="forensics-listing-id">
              ✓ {listingId}
            </div>
          )}
          {suggest.length > 0 && !listingId && (
            <div className="absolute z-10 left-0 right-0 mt-1 border border-border rounded-md bg-popover shadow-lg overflow-hidden">
              {suggest.map((l) => (
                <button
                  key={l.id}
                  onClick={() => pickListing(l)}
                  data-testid={`forensics-suggest-${l.id}`}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 border-b border-border last:border-0"
                >
                  <span className="font-medium">{l.title}</span>
                  <span className="ml-2 text-[10px] font-mono uppercase text-muted-foreground">{l.file_type}</span>
                </button>
              ))}
            </div>
          )}
          {q.length >= 2 && !listingId && !suggest.length && (
            <button
              onClick={() => setListingId(q.trim())}
              className="mt-2 text-xs text-primary hover:underline font-mono"
              data-testid="forensics-use-raw-id"
            >
              Use "{q.trim()}" as raw listing id →
            </button>
          )}
        </div>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="forensics-dropzone"
          className={`border-2 border-dashed rounded-md p-8 text-center transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border"}`}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <UploadCloud className="h-5 w-5 text-emerald-500" />
              <div className="text-left">
                <div className="text-sm font-medium" data-testid="forensics-file-name">{file.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <button onClick={() => setFile(null)} className="p-1 rounded hover:bg-secondary" data-testid="forensics-clear-file">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label className="cursor-pointer inline-flex flex-col items-center gap-2">
              <UploadCloud className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm">Drop STL here, or <span className="text-primary underline">browse</span></span>
              <span className="text-[11px] font-mono text-muted-foreground">.stl only · up to 200 MB</span>
              <input
                type="file"
                accept=".stl,application/octet-stream"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="forensics-file-input"
              />
            </label>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={busy || !file || !listingId}
            data-testid="forensics-submit"
            className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Analysing…</> : <>Trace leak</>}
          </button>
          {result && (
            <button onClick={reset} data-testid="forensics-reset" className="border border-border rounded-md px-5 py-2.5 text-sm">
              New scan
            </button>
          )}
        </div>
      </div>

      {result && (
        <div className="border border-border rounded-md bg-card p-6 space-y-4" data-testid="forensics-result">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h3 className="font-display font-semibold">Ranked candidates</h3>
            <div className="text-xs font-mono text-muted-foreground">
              {result.candidate_count} candidate{result.candidate_count === 1 ? "" : "s"} · {result.matches?.length || 0} scored
            </div>
          </div>
          {result.note && (
            <div className="text-xs bg-secondary/60 border border-border rounded p-3 font-mono leading-relaxed">
              {result.note}
            </div>
          )}
          {result.matches?.length > 0 && (
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left">
                  <tr>
                    {["Rank", "Buyer", "Signal", "Score", "Subject"].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-[11px] uppercase font-mono text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.matches.map((m, i) => {
                    const signal = Number(m.signal || 0);
                    const strong = signal > 0.5;
                    return (
                      <tr key={m.subject} data-testid={`forensics-match-${i}`} className={`border-t border-border ${i === 0 && strong ? "bg-emerald-500/5" : ""}`}>
                        <td className="px-4 py-2.5 font-mono text-xs">#{i + 1}</td>
                        <td className="px-4 py-2.5">
                          {m.name || <span className="text-muted-foreground italic">anon browser</span>}
                          {m.email && <div className="text-[11px] font-mono text-muted-foreground">{m.email}</div>}
                        </td>
                        <td className="px-4 py-2.5 font-mono">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 rounded-full bg-secondary overflow-hidden">
                              <div
                                className={strong ? "bg-emerald-500 h-full" : "bg-amber-500 h-full"}
                                style={{ width: `${Math.min(100, Math.max(2, signal * 100)).toFixed(0)}%` }}
                              />
                            </div>
                            <span className={`text-xs ${strong ? "text-emerald-500" : "text-muted-foreground"}`}>{signal.toFixed(4)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{Number(m.score || 0).toExponential(2)}</td>
                        <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground break-all">{m.subject}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ isSuper }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(apiErr(e)); } };
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="space-y-6">
      <div className="max-w-lg border border-border rounded-md bg-card p-6 space-y-4">
        <h2 className="font-display font-semibold text-lg">Platform settings</h2>
        <label className="block"><span className="text-sm font-medium">Platform commission (%)</span><input type="number" value={s.commission} onChange={(e) => setS({ ...s, commission: Number(e.target.value) })} disabled={!isSuper} data-testid="setting-commission" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" /></label>
        <label className="block"><span className="text-sm font-medium">Escrow hold (hours)</span><input type="number" value={s.hold_hours} onChange={(e) => setS({ ...s, hold_hours: Number(e.target.value) })} disabled={!isSuper} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" /></label>
        <label className="block"><span className="text-sm font-medium">Download limit per order (0 = unlimited)</span><input type="number" value={s.download_limit} onChange={(e) => setS({ ...s, download_limit: Number(e.target.value) })} disabled={!isSuper} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.auto_publish} onChange={(e) => setS({ ...s, auto_publish: e.target.checked })} disabled={!isSuper} data-testid="setting-autopublish" /> Auto-publish new listings (skip manual review)</label>
        {isSuper && <button onClick={save} data-testid="save-settings" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold">Save settings</button>}
      </div>
      <AutoDelistPanel isSuper={isSuper} />
      <RenderPresetsPanel isSuper={isSuper} />
      <TwoFactorPanel />
    </div>
  );
}

// 2026-03-01 — Admin 2FA enrolment. Any admin/finance user must enable
// this before they can view sensitive KYC data (decrypted PAN, bank
// account, PAN image download). Uses the /admin/2fa/setup + verify-setup
// backend endpoints, TOTP via any authenticator app (Google, Microsoft,
// 1Password, Authy). Once enabled it can only be reset by another
// super-admin (out of scope for this panel).
function TwoFactorPanel() {
  const [status, setStatus] = useState(null);  // { enabled: bool }
  const [setup, setSetup] = useState(null);    // { secret, otpauth_uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/2fa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  useEffect(() => { load(); }, []);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your authenticator app.");
    setBusy(true);
    try {
      await api.post("/admin/2fa/verify-setup", { code });
      toast.success("Two-factor authentication is now active on your admin account.");
      setSetup(null); setCode(""); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg border border-border rounded-md bg-card p-6 space-y-4" data-testid="admin-2fa-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Two-factor authentication</h2>
          <p className="text-xs text-muted-foreground mt-1">Required for viewing sensitive KYC data (decrypted PAN, bank account number, PAN card image). Uses any TOTP authenticator app.</p>
        </div>
        {status?.enabled && <span data-testid="admin-2fa-enabled-chip" className="text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-2 py-0.5 self-start">✓ Enabled</span>}
      </div>
      {status?.enabled ? (
        <p className="text-xs text-muted-foreground">2FA is active. To reset (lost phone / new device), ask another super-admin to clear your enrolment.</p>
      ) : !setup ? (
        <button onClick={start} disabled={busy} data-testid="admin-2fa-start" className="bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
          {busy ? "Preparing…" : "Enable authenticator app"}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="border border-dashed border-primary/40 bg-primary/5 rounded p-3 space-y-2">
            <p className="text-xs font-semibold">1. Add the account to your authenticator app</p>
            <p className="text-[11px] text-muted-foreground">Scan the QR (below) or paste this key manually into Google / Microsoft / 1Password / Authy:</p>
            <div className="font-mono text-xs bg-white border border-border rounded p-2 select-all break-all" data-testid="admin-2fa-secret">{setup.secret}</div>
            {/* Free public QR code from otpauth URI — no external JS lib needed */}
            <img
              alt="TOTP QR code"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_uri)}`}
              className="w-44 h-44 border border-border rounded bg-white"
              data-testid="admin-2fa-qr"
            />
          </div>
          <div className="border border-border rounded p-3 space-y-2">
            <p className="text-xs font-semibold">2. Enter the current 6-digit code from your app</p>
            <div className="flex gap-2">
              <input
                inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="admin-2fa-code-input"
                placeholder="123 456"
                className="border border-border rounded px-3 py-2 text-sm font-mono text-center tracking-widest w-32 bg-background"
              />
              <button onClick={verify} disabled={busy || code.length !== 6} data-testid="admin-2fa-verify" className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                {busy ? "Verifying…" : "Activate 2FA"}
              </button>
              <button onClick={() => { setSetup(null); setCode(""); }} className="border border-border rounded px-3 py-2 text-sm hover:bg-secondary">Cancel</button>
            </div>
            <p className="text-[10px] text-muted-foreground italic">Codes rotate every 30 seconds — grab a fresh one if it fails.</p>
          </div>
        </div>
      )}
    </div>
  );
}


// Photo Audit — scans every listing for missing/broken studio photos and
// lets admin bulk-repair the flagged ones via /api/admin/listings/photo-audit
// and /api/admin/listings/photo-audit/repair.
function PhotoAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [repairing, setRepairing] = useState(false);
  const [onlyBroken, setOnlyBroken] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/listings/photo-audit?only_broken=${onlyBroken}&limit=500`);
      setData(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [onlyBroken]); // eslint-disable-line

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => {
    if (!data) return;
    if (selected.size === data.rows.length) setSelected(new Set());
    else setSelected(new Set(data.rows.map((r) => r.id)));
  };

  const repair = async () => {
    if (!selected.size) return toast.error("Select at least one listing to repair.");
    if (!window.confirm(`Re-render studio photos for ${selected.size} listing(s)? Runs in background — status will refresh in ~20-30 s.`)) return;
    setRepairing(true);
    try {
      const r = await api.post("/admin/listings/photo-audit/repair", { listing_ids: [...selected] });
      const { queued, skipped } = r.data;
      toast.success(`${queued.length} queued for re-render${skipped.length ? ` · ${skipped.length} skipped (no source STL)` : ""}. Refreshing in 25 s…`);
      setTimeout(load, 25000);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRepairing(false); }
  };

  return (
    <div className="space-y-4" data-testid="photo-audit">
      <div className="border border-border rounded-md bg-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display font-bold text-lg">Photo Audit</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every listing scanned for missing / unreachable studio photos. Select rows and click <b>Repair</b> to re-render them server-side.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={onlyBroken}
                onChange={(e) => setOnlyBroken(e.target.checked)}
                data-testid="pa-only-broken"
              />
              Only show broken
            </label>
            <button onClick={load} disabled={loading} className="text-xs border border-border rounded-md px-3 py-1.5 hover:bg-secondary disabled:opacity-60" data-testid="pa-refresh">
              {loading ? <Loader2 className="h-3 w-3 animate-spin inline" /> : <RefreshCw className="h-3 w-3 inline" />} Refresh
            </button>
          </div>
        </div>
        {data && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KPI label="Scanned" value={data.scanned} />
            <KPI label="Broken" value={data.broken_count} accent="text-red-500" />
            <KPI label="Healthy" value={data.scanned - data.broken_count} accent="text-emerald-500" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="border border-border rounded-md py-16 text-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Scanning catalog…
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="border border-border rounded-md py-16 text-center text-emerald-600" data-testid="pa-empty">
          <CheckCircle2 className="h-6 w-6 inline mr-2" /> No broken listings found.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between border border-border rounded-md bg-card p-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.size === data.rows.length} onChange={selectAll} data-testid="pa-select-all" />
              {selected.size ? `${selected.size} selected` : `Select all ${data.rows.length}`}
            </label>
            <button
              onClick={repair}
              disabled={!selected.size || repairing}
              data-testid="pa-repair"
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Repair selected
            </button>
          </div>
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-secondary/50 text-left">
                <tr>
                  {["", "Listing", "Seller", "Status", "Angles", "Problems", "Created"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`pa-row-${r.id}`}>
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`pa-toggle-${r.id}`} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium truncate max-w-xs">{r.title || r.id}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{r.id}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <div>{r.seller_name || "—"}</div>
                      <div className="text-muted-foreground">{r.seller_email || ""}</div>
                    </td>
                    <td className="px-3 py-2.5"><StatusTag status={r.status} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs">
                      {r.angles_total - r.angles_missing}/{r.angles_total}
                      {r.angles_missing > 0 && <span className="text-red-500"> (-{r.angles_missing})</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-red-500">
                      {(r.problems || []).join("; ") || <span className="text-emerald-500">OK</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">
                      {r.created_at ? String(r.created_at).slice(0, 10) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// Auto-delist thresholds — nightly sweeper marks low-performers, notifies
// sellers, then unpublishes them after the notice window. Backend:
// GET/PUT /api/admin/auto-delist/config, POST /api/admin/auto-delist/sweep.
function AutoDelistPanel({ isSuper }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);
  useEffect(() => { api.get("/admin/auto-delist/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/auto-delist/config", cfg);
      setCfg(data);
      toast.success("Auto-delist settings saved.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSaving(false); }
  };
  const runNow = async () => {
    if (!window.confirm("Run the auto-delist sweep against ALL live listings right now?")) return;
    setSweeping(true);
    try {
      const { data } = await api.post("/admin/auto-delist/sweep");
      setLastSweep(data);
      if (data.skipped) {
        toast.info("Sweep skipped — auto-delist is currently disabled.");
      } else {
        toast.success(`Sweep complete — warned ${data.warned || 0}, delisted ${data.delisted || 0}.`);
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSweeping(false); }
  };
  if (!cfg) return <div className="max-w-lg border border-border rounded-md bg-card p-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  return (
    <div className="max-w-2xl border border-border rounded-md bg-card p-6 space-y-4" data-testid="auto-delist-panel">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Auto-delist low performers</h2>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          A nightly sweeper flags approved listings older than <b>X days</b> with fewer than <b>Y views</b> and <b>Z sales</b>. Sellers get a warning with a <b>notice window</b>; if performance doesn't improve, the listing is unpublished automatically. Individual listings can be exempted from the Moderation view.
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm p-3 border border-border rounded-md">
        <input type="checkbox" checked={!!cfg.auto_delist_enabled} onChange={(e) => set("auto_delist_enabled", e.target.checked)} disabled={!isSuper} data-testid="delist-enabled" />
        <span className="font-medium">Enable nightly auto-delist sweep</span>
        <span className={`ml-auto text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${cfg.auto_delist_enabled ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-border text-muted-foreground"}`}>
          {cfg.auto_delist_enabled ? "on" : "off"}
        </span>
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Minimum age (days)</span>
          <input type="number" min="0" value={cfg.auto_delist_age_days ?? 60} onChange={(e) => set("auto_delist_age_days", Number(e.target.value))} disabled={!isSuper} data-testid="delist-age" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          <span className="text-[10px] text-muted-foreground">Only listings older than this are considered.</span>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notice window (days)</span>
          <input type="number" min="0" value={cfg.auto_delist_notice_days ?? 10} onChange={(e) => set("auto_delist_notice_days", Number(e.target.value))} disabled={!isSuper} data-testid="delist-notice" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          <span className="text-[10px] text-muted-foreground">Warning → delist grace period.</span>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Min. views threshold</span>
          <input type="number" min="0" value={cfg.auto_delist_min_views ?? 50} onChange={(e) => set("auto_delist_min_views", Number(e.target.value))} disabled={!isSuper} data-testid="delist-views" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          <span className="text-[10px] text-muted-foreground">Under this = low-perf.</span>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Min. sales threshold</span>
          <input type="number" min="0" value={cfg.auto_delist_min_sales ?? 0} onChange={(e) => set("auto_delist_min_sales", Number(e.target.value))} disabled={!isSuper} data-testid="delist-sales" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          <span className="text-[10px] text-muted-foreground">At or below this = low-perf.</span>
        </label>
      </div>
      {isSuper && (
        <div className="flex flex-wrap gap-3 pt-2 border-t border-border">
          <button onClick={save} disabled={saving} data-testid="delist-save" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save auto-delist settings
          </button>
          <button onClick={runNow} disabled={sweeping} data-testid="delist-run-now" className="border border-amber-500/40 text-amber-600 rounded-md px-4 py-2 text-sm font-semibold hover:bg-amber-500/5 disabled:opacity-60 flex items-center gap-2">
            {sweeping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run sweep now
          </button>
        </div>
      )}
      {lastSweep && (
        <div className="mt-2 rounded-md border border-border bg-secondary/40 p-3 text-xs font-mono" data-testid="delist-sweep-result">
          {lastSweep.skipped ? (
            <span>Sweep skipped — auto-delist is disabled.</span>
          ) : (
            <span>Last sweep: warned <b>{lastSweep.warned || 0}</b> · delisted <b>{lastSweep.delisted || 0}</b></span>
          )}
        </div>
      )}
    </div>
  );
}

// RenderPresetsPanel — admin-tweakable studio-render defaults. Values persist
// in `settings` and take effect on the very next render call (no restart).
// Ship defaults: #A5A5A5 object on #FFFFFF background, matte finish, 85% fill.
function RenderPresetsPanel({ isSuper }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/render/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/render/config", cfg);
      setCfg(data);
      toast.success("Render presets saved. Next render will use these values.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSaving(false); }
  };
  if (!cfg) return <div className="max-w-2xl border border-border rounded-md bg-card p-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const objectHex = cfg.render_object_color_hex || "#C8CCD0";
  const bgHex = cfg.render_bg_color_hex || "#0F0F10";
  return (
    <div className="max-w-2xl border border-border rounded-md bg-card p-6 space-y-4" data-testid="render-presets-panel">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" /> Render presets
        </h2>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          These values are used by the studio renderer whenever a seller uploads a new design
          or triggers "Regenerate 8 photos" from the PhotoManager. Changes take effect on the
          very next render — no restart required.
        </p>
      </div>

      {/* Live 3D preview — TWO panels side by side so admins can compare
          the server pyrender output (left) against the browser three.js
          output (right). Both read the SAME `cfg` state so colour drift
          is impossible by construction. This is the "3-second in-browser
          preview" the spec calls out — no seller upload required. */}
      <div className="flex flex-wrap gap-6">
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Server render</p>
          <RenderPreviewSwatch cfg={cfg} testid="render-preview-swatch" />
        </div>
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Client render (what sellers upload)</p>
          <LiveClientPreview cfg={cfg} testid="live-client-preview" />
        </div>
      </div>

      {/* Reset-to-defaults — the escape hatch for environments (esp.
          production) whose Mongo still carries pre-2026-02 override rows
          from a previous admin save. Clicking wipes every render_* row
          so the container's baked-in RenderConfig (silver #C8CCD0 on
          near-black #0F0F10, AO 0.90, satin 0.30) becomes effective on
          the very next render. Idempotent — safe to click on a clean env. */}
      {isSuper && (
        <div className="flex items-center justify-end -mt-2 mb-2">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(
                "Reset render presets to the 2026-02 reference defaults? " +
                "This clears every custom colour/AO/light value in the settings collection. " +
                "You can re-save your own values immediately after."
              )) return;
              try {
                const { data } = await api.post("/admin/render/reset-defaults");
                setCfg({ ...cfg, ...(data.config || {}) });
                toast.success(`Presets reset — ${data.cleared} custom row(s) cleared.`);
              } catch (e) {
                toast.error(apiErr(e));
              }
            }}
            data-testid="render-reset-defaults-btn"
            title="Wipe every custom render_* row so the container's baked-in 2026-02 defaults take effect"
            className="inline-flex items-center gap-1.5 border border-amber-500/50 text-amber-600 rounded-md px-3 py-1.5 text-[11px] font-semibold hover:bg-amber-500/5"
          >
            <RefreshCw className="h-3 w-3" /> Reset to 2026-02 reference defaults
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Object colour</span>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              value={objectHex}
              onChange={(e) => set("render_object_color_hex", e.target.value.toUpperCase())}
              disabled={!isSuper}
              data-testid="render-object-color"
              className="h-10 w-14 border border-border rounded bg-background cursor-pointer"
            />
            <input
              type="text"
              value={objectHex}
              onChange={(e) => set("render_object_color_hex", e.target.value)}
              disabled={!isSuper}
              className="flex-1 border border-border rounded-md p-2.5 text-sm bg-background font-mono"
            />
          </div>
          <span className="text-[10px] text-muted-foreground">2026-02 default #C8CCD0 (silver). Try #E5E4C0 for gold, #A5A5A5 for lead grey.</span>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Background colour</span>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              value={bgHex}
              onChange={(e) => set("render_bg_color_hex", e.target.value.toUpperCase())}
              disabled={!isSuper}
              data-testid="render-bg-color"
              className="h-10 w-14 border border-border rounded bg-background cursor-pointer"
            />
            <input
              type="text"
              value={bgHex}
              onChange={(e) => set("render_bg_color_hex", e.target.value)}
              disabled={!isSuper}
              className="flex-1 border border-border rounded-md p-2.5 text-sm bg-background font-mono"
            />
          </div>
          <span className="text-[10px] text-muted-foreground">Default #FFFFFF (white). Try #F8FAFC for soft off-white.</span>
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Material finish (specular)</span>
          <input
            type="range" min="0" max="1" step="0.05"
            value={cfg.render_object_specular ?? 0.35}
            onChange={(e) => set("render_object_specular", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-specular"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Matte</span>
            <span>{(cfg.render_object_specular ?? 0.35).toFixed(2)}</span>
            <span>Mirror</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Highlight sharpness</span>
          <input
            type="range" min="1" max="128" step="1"
            value={cfg.render_object_shininess ?? 24}
            onChange={(e) => set("render_object_shininess", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-shininess"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Soft</span>
            <span>{Math.round(cfg.render_object_shininess ?? 24)}</span>
            <span>Sharp</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Object framing</span>
          <input
            type="range" min="0.4" max="0.98" step="0.01"
            value={cfg.render_frame_fill ?? 0.85}
            onChange={(e) => set("render_frame_fill", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-frame-fill"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Smaller</span>
            <span>{((cfg.render_frame_fill ?? 0.85) * 100).toFixed(0)}%</span>
            <span>Larger</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Contact shadow</span>
          <input
            type="range" min="0" max="255" step="5"
            value={cfg.render_shadow_opacity ?? 55}
            onChange={(e) => set("render_shadow_opacity", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-shadow"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>None</span>
            <span>{Math.round(cfg.render_shadow_opacity ?? 55)}</span>
            <span>Deep</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Metallic (0 = matte, 1 = pure metal)</span>
          <input
            type="range" min="0" max="1" step="0.05"
            value={cfg.render_object_metallic ?? 0.05}
            onChange={(e) => set("render_object_metallic", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-metallic"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Matte</span>
            <span>{(cfg.render_object_metallic ?? 0.05).toFixed(2)}</span>
            <span>Chrome</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Ambient occlusion strength</span>
          <input
            type="range" min="0" max="1" step="0.05"
            value={cfg.render_ao_strength ?? 0.55}
            onChange={(e) => set("render_ao_strength", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-ao"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Flat</span>
            <span>{(cfg.render_ao_strength ?? 0.55).toFixed(2)}</span>
            <span>Deep shadows</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Lighting intensity</span>
          <input
            type="range" min="0.4" max="1.15" step="0.05"
            value={cfg.render_light_intensity ?? 0.85}
            onChange={(e) => set("render_light_intensity", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-light-intensity"
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>Moody</span>
            <span>{(cfg.render_light_intensity ?? 0.85).toFixed(2)}</span>
            <span>Bright</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Render resolution</span>
          <select
            value={cfg.render_output_size ?? 1600}
            onChange={(e) => set("render_output_size", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-output-size"
            className="mt-2 w-full border border-border rounded-md p-2 text-sm bg-background font-mono"
          >
            <option value={800}>800 × 800</option>
            <option value={1200}>1200 × 1200</option>
            <option value={1600}>1600 × 1600 (recommended)</option>
            <option value={2048}>2048 × 2048 (slowest)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Anti-aliasing (SSAA)</span>
          <select
            value={cfg.render_ssaa ?? 2}
            onChange={(e) => set("render_ssaa", Number(e.target.value))}
            disabled={!isSuper}
            data-testid="render-ssaa"
            className="mt-2 w-full border border-border rounded-md p-2 text-sm bg-background font-mono"
          >
            <option value={1}>1× (fastest, aliased)</option>
            <option value={2}>2× (recommended)</option>
            <option value={3}>3× (sharper, +4× time)</option>
            <option value={4}>4× (highest, +16× time)</option>
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Number of angles</span>
          <div className="mt-2 flex items-center gap-3">
            <span className="rounded-md border border-border px-3 py-2 text-sm font-mono bg-secondary/40 min-w-[3rem] text-center">8</span>
            <span className="text-[11px] text-muted-foreground">Front · back · left · right · top · bottom · two 3/4 views</span>
          </div>
        </label>
      </div>

      {isSuper && (
        <div className="flex flex-wrap gap-3 pt-2 border-t border-border">
          <button onClick={save} disabled={saving} data-testid="render-save" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save render presets
          </button>
          <button
            onClick={() => setCfg({
              // Ship defaults synced with the matte pyrender pipeline
              // (2026-02-21 rewrite). Matches the CAD-viewport reference
              // the user picked: flat matte object on solid background,
              // uniform ambient + soft directional fill, no contact
              // shadow, 1600×1600 SSAA=2. Do not drift from `RenderConfig`
              // defaults in /app/backend/render_studio.py.
              render_object_color_hex: "#C8CCD0",
              render_bg_color_hex: "#0F0F10",
              render_object_specular: 0.08,
              render_object_shininess: 20,
              render_object_metallic: 0.00,
              render_ao_strength: 0.55,
              render_frame_fill: 0.85,
              render_shadow_opacity: 0,
              render_proxy_target_faces: 350000,
              render_output_size: 1600,
              render_ssaa: 2,
              render_light_intensity: 0.85,
            })}
            data-testid="render-reset"
            className="border border-border rounded-md px-4 py-2 text-sm font-semibold hover:bg-secondary"
          >
            Reset to ship defaults
          </button>
        </div>
      )}
    </div>
  );
}



// FAQ Editor — content-editor role can add / update / delete / reorder /
// activate FAQ items. Slugs are re-slugified server-side; edits are logged
// to the audit trail.
const FAQ_SECTIONS = [
  "About Jeweltix", "Buying CAD Files", "File Formats", "Selling on Jeweltix",
  "Payments", "3D Printing and CAM Vendors", "Support",
];

function FaqEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/admin/faq").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const bySection = FAQ_SECTIONS.map((s) => ({
    section: s,
    items: items.filter((i) => i.section === s).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  }));
  const extraSections = [...new Set(items.map((i) => i.section))].filter((s) => !FAQ_SECTIONS.includes(s));
  extraSections.forEach((s) => bySection.push({
    section: s,
    items: items.filter((i) => i.section === s).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  }));

  const toggleActive = async (item) => {
    try {
      await api.put(`/admin/faq/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.question}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/faq/${item.id}`);
      toast.success("FAQ deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const move = async (item, direction) => {
    const list = items.filter((i) => i.section === item.section).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = list.findIndex((i) => i.id === item.id);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try {
      await api.post("/admin/faq/reorder", {
        section: item.section,
        ids_in_order: reordered.map((i) => i.id),
      });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="faq-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" /> FAQ Editor
          </h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            Content on <a href="/faq" target="_blank" rel="noreferrer" className="text-primary hover:underline">/faq</a> and every contextual FAQ block on the site is drawn from this list. Edits go live instantly — no code deploy needed. Slugs power the anchor links and SEO citation URLs.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="faq-add"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Add question
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading FAQs…
        </div>
      ) : (
        <div className="space-y-8">
          {bySection.map((sec) => sec.items.length > 0 && (
            <div key={sec.section} data-testid={`faq-editor-section-${sec.section.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <h3 className="font-display font-semibold text-primary text-base mb-3">{sec.section}
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground ml-2">({sec.items.length})</span>
              </h3>
              <div className="border border-border rounded-md bg-card divide-y divide-border">
                {sec.items.map((it, i) => (
                  <div key={it.id} className="p-3 sm:p-4 flex items-start gap-3" data-testid={`faq-editor-row-${it.slug}`}>
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => move(it, "up")} disabled={i === 0} className="p-0.5 hover:text-primary disabled:opacity-30" data-testid={`faq-up-${it.slug}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => move(it, "down")} disabled={i === sec.items.length - 1} className="p-0.5 hover:text-primary disabled:opacity-30" data-testid={`faq-down-${it.slug}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <p className="font-medium text-sm">{it.question}</p>
                        {!it.is_active && <span className="text-[10px] font-mono uppercase tracking-widest bg-red-500/10 text-red-500 border border-red-500/30 rounded-full px-2 py-0.5">hidden</span>}
                      </div>
                      <p className="text-[11px] text-muted-foreground font-mono truncate">/faq#{it.slug}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.answer}</p>
                      {(it.helpful_count > 0 || it.unhelpful_count > 0) && (
                        <p className="text-[10px] text-muted-foreground font-mono mt-1">
                          👍 {it.helpful_count || 0} · 👎 {it.unhelpful_count || 0}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => toggleActive(it)} data-testid={`faq-toggle-${it.slug}`} title={it.is_active ? "Hide" : "Show"} className="border border-border rounded-md px-2.5 py-1.5 text-[11px] font-mono uppercase hover:bg-secondary">{it.is_active ? "hide" : "show"}</button>
                      <button onClick={() => setEditing(it)} data-testid={`faq-edit-${it.slug}`} className="border border-primary/40 text-primary rounded-md px-2.5 py-1.5 text-[11px] font-mono uppercase hover:bg-primary/5">edit</button>
                      <button onClick={() => remove(it)} data-testid={`faq-delete-${it.slug}`} title="Delete" className="border border-red-500/40 text-red-500 rounded-md px-2 py-1.5 hover:bg-red-500/5"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || creating) && (
        <FaqEditModal
          item={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function FaqEditModal({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState({
    section: item?.section || FAQ_SECTIONS[0],
    slug: item?.slug || "",
    question: item?.question || "",
    answer: item?.answer || "",
    is_active: item?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.question.trim() || !form.answer.trim() || !form.section) {
      toast.error("Section, question and answer are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        section: form.section,
        slug: form.slug || form.question,
        question: form.question,
        answer: form.answer,
        is_active: form.is_active,
      };
      if (isNew) {
        await api.post("/admin/faq", payload);
        toast.success("FAQ created");
      } else {
        await api.put(`/admin/faq/${item.id}`, payload);
        toast.success("FAQ updated");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[9997] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="faq-edit-modal">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            <h3 className="font-display font-bold text-lg">{isNew ? "Add FAQ" : "Edit FAQ"}</h3>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Section</span>
            <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} data-testid="faq-form-section" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background">
              {FAQ_SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Question</span>
            <input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-form-question" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">URL slug (blank auto-generates from question)</span>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="faq-form-slug" placeholder="how-do-designers-get-paid" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Answer</span>
            <textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={7} data-testid="faq-form-answer" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-sans" />
            <span className="text-[10px] text-muted-foreground">Use plain text. Inline links via markdown: <code>[label](/path)</code></span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="faq-form-active" />
            <span>Visible on /faq and contextual blocks</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="faq-form-save" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Create FAQ" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}



const MACHINE_LABEL = {
  sla: "SLA", dlp: "DLP", msla: "MSLA", wax_jet: "Wax-Jet / MJP", fdm: "FDM",
};
const WL_STATUS_TABS = [
  { key: "pending", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "onboarded", label: "Onboarded" },
  { key: "rejected", label: "Rejected" },
];
const WL_STATUS_STYLE = {
  pending: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  contacted: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  onboarded: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-500 border-red-500/30",
};

function VendorWaitlist() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // id of the row currently being mutated
  const [magic, setMagic] = useState(null); // {shop_name, email, magic_link, emailed} when we just onboarded

  const load = () => {
    setLoading(true);
    api.get("/admin/vendor-waitlist", { params: { status: tab } })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Failed to load waitlist"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const setStatus = async (id, status, row) => {
    setBusy(id);
    try {
      const { data } = await api.patch(`/admin/vendor-waitlist/${id}`, { status });
      toast.success(`Marked as ${status}`);
      // If the transition created a vendor account with a magic link, surface it now.
      if (status === "onboarded" && data?.magic_link) {
        setMagic({ shop_name: row?.shop_name, email: row?.email, magic_link: data.magic_link, emailed: data.emailed });
      }
      // Optimistically drop from the current view so admin sees progress instantly.
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(null);
    }
  };

  const counts = rows.length;

  return (
    <div className="space-y-5" data-testid="vendor-waitlist-tab">
      <div>
        <h2 className="font-display font-semibold text-lg flex items-center gap-2">
          <Printer className="h-5 w-5 text-primary" /> CAM Vendor Waitlist
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
          Applications from print shops via the "Partner with us" landing page. Contact each vendor, verify their machinery, then mark <em className="not-italic text-foreground">Onboarded</em> once their account is live.
        </p>
      </div>

      {/* status tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {WL_STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`wl-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
            {tab === t.key && <span className="ml-2 text-[10px] font-mono text-muted-foreground">({counts})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-16 text-center text-sm text-muted-foreground" data-testid="wl-empty">
          No {tab} applications right now.
        </div>
      ) : (
        <div className="grid gap-3">
          {rows.map((v) => (
            <div key={v.id} className="border border-border rounded-md bg-card p-4 sm:p-5" data-testid={`waitlist-row-${v.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-base">{v.shop_name}</p>
                    <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${WL_STATUS_STYLE[v.status] || ""}`}>
                      {v.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {v.owner_name} · Applied {new Date(v.created_at).toLocaleDateString()}
                  </p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    <a href={`mailto:${v.email}`} className="flex items-center gap-2 text-foreground hover:text-primary truncate" data-testid={`wl-email-${v.id}`}>
                      <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{v.email}</span>
                    </a>
                    <a href={`tel:${v.mobile}`} className="flex items-center gap-2 text-foreground hover:text-primary" data-testid={`wl-phone-${v.id}`}>
                      <Phone className="h-3.5 w-3.5 shrink-0" /> {v.mobile}
                    </a>
                    <span className="flex items-center gap-2 text-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" /> {v.city}
                    </span>
                  </div>
                  {v.machines?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {v.machines.map((m) => (
                        <span key={m} className="text-[10px] font-mono uppercase tracking-widest bg-secondary border border-border rounded-full px-2 py-0.5">
                          {MACHINE_LABEL[m] || m}
                        </span>
                      ))}
                    </div>
                  )}
                  {v.message && (
                    <p className="mt-3 text-xs text-muted-foreground bg-secondary/40 border border-border rounded p-2.5 leading-relaxed">
                      "{v.message}"
                    </p>
                  )}
                </div>

                {/* action buttons */}
                <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                  {v.status !== "contacted" && (
                    <button
                      onClick={() => setStatus(v.id, "contacted", v)}
                      disabled={busy === v.id}
                      data-testid={`wl-mark-contacted-${v.id}`}
                      className="flex items-center justify-center gap-1.5 border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {busy === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Phone className="h-3 w-3" />}
                      Contacted
                    </button>
                  )}
                  {v.status !== "onboarded" && (
                    <button
                      onClick={() => setStatus(v.id, "onboarded", v)}
                      disabled={busy === v.id}
                      data-testid={`wl-mark-onboarded-${v.id}`}
                      className="flex items-center justify-center gap-1.5 border border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {busy === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      Onboarded
                    </button>
                  )}
                  {v.status !== "rejected" && (
                    <button
                      onClick={() => setStatus(v.id, "rejected", v)}
                      disabled={busy === v.id}
                      data-testid={`wl-mark-rejected-${v.id}`}
                      className="flex items-center justify-center gap-1.5 border border-red-500/40 text-red-600 hover:bg-red-500/10 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {busy === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                      Reject
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Post-onboarding modal: magic link the admin can copy/share */}
      <Dialog open={!!magic} onOpenChange={(o) => !o && setMagic(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> {magic?.shop_name} onboarded</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">
            A CAM vendor account has been created for <strong className="text-foreground">{magic?.email}</strong>. Share the one-time login link below so they can set up their shop. The link is valid for 48 hours and can only be used once.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={magic?.magic_link || ""}
              data-testid="magic-link-input"
              className="flex-1 border border-border rounded-md p-2.5 text-xs font-mono bg-secondary/40"
              onClick={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(magic?.magic_link || ""); toast.success("Link copied"); }}
              data-testid="magic-link-copy"
              className="bg-primary text-primary-foreground rounded-md px-4 text-sm font-semibold"
            >
              Copy
            </button>
          </div>
          {magic?.emailed ? (
            <p className="mt-3 text-xs text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Welcome email sent to {magic.email}</p>
          ) : (
            <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5" /> Email not sent (Resend not configured). Please share the link manually.</p>
          )}
          <DialogFooter>
            <button onClick={() => setMagic(null)} className="border border-border rounded-md px-4 py-2 text-sm">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// --- Legal & Grievance: T&C versions, platform config, acceptance audit, grievances ---
function LegalTab() {
  const [sub, setSub] = useState("config");
  const TABS = [
    { key: "config", label: "Platform Config" },
    { key: "versions", label: "T&C Versions" },
    { key: "acceptances", label: "Acceptance Audit" },
    { key: "grievances", label: "Grievances" },
  ];
  return (
    <div className="space-y-5" data-testid="legal-tab">
      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setSub(t.key)}
            data-testid={`legal-sub-${t.key}`}
            className={`text-sm px-4 py-2 border-b-2 -mb-px ${sub === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "config" && <PlatformConfigEditor />}
      {sub === "versions" && <TermsVersions />}
      {sub === "acceptances" && <AcceptanceAudit />}
      {sub === "grievances" && <GrievanceInbox />}
    </div>
  );
}

function PlatformConfigEditor() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform-config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="text-sm text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/platform-config", cfg);
      toast.success("Platform config saved.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const F = ({ label, k, textarea }) => (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} rows={2} data-testid={`config-${k}`} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
      ) : (
        <input value={cfg[k] || ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} data-testid={`config-${k}`} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
      )}
    </label>
  );

  return (
    <div className="max-w-2xl border border-border rounded-md bg-card p-6 space-y-4">
      <div>
        <h3 className="font-display font-bold text-lg">Platform legal fields</h3>
        <p className="text-xs text-muted-foreground mt-1">Edit the Grievance Officer, registered address and legal entity name. Changes propagate to every T&amp;C rendering in real time — no code deploy needed.</p>
      </div>
      <F label="Legal entity name" k="legal_entity" />
      <div className="grid grid-cols-2 gap-3">
        <F label="Jurisdiction city" k="jurisdiction_city" />
        <F label="Jurisdiction state" k="jurisdiction_state" />
      </div>
      <F label="Grievance Officer name" k="grievance_officer_name" />
      <F label="Grievance Officer email" k="grievance_officer_email" />
      <F label="Registered address" k="registered_address" textarea />
      <button onClick={save} disabled={busy} data-testid="save-platform-config" className="bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
      </button>
    </div>
  );
}

function TermsVersions() {
  const [rows, setRows] = useState(null);
  const [pending, setPending] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState({ role: "buyer", version: "", changelog: "" });

  const load = () => {
    api.get("/admin/terms/versions").then((r) => setRows(r.data));
    api.get("/admin/terms/pending-reacceptance").then((r) => setPending(r.data)).catch(() => setPending(null));
  };
  useEffect(load, []);

  const publish = async () => {
    if (!form.version.trim()) return toast.error("Enter a version number (e.g. 1.1)");
    setPublishing(true);
    try {
      await api.post("/admin/terms/publish", { role: form.role, version: form.version.trim(), changelog: form.changelog });
      toast.success(`Published ${form.role} v${form.version}. Users will be prompted to re-accept on next login.`);
      setForm({ role: form.role, version: "", changelog: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPublishing(false); }
  };

  return (
    <div className="space-y-5" data-testid="terms-versions">
      {pending && (
        <div className="border border-amber-500/30 bg-amber-500/10 rounded-md p-4 text-sm text-amber-900">
          <strong>{pending.pending_count}</strong> of {pending.total_active} active users are pending re-acceptance.
        </div>
      )}
      <div className="border border-border rounded-md bg-card p-5 max-w-xl">
        <h3 className="font-display font-bold text-lg mb-3">Publish new version</h3>
        <p className="text-xs text-muted-foreground mb-4">Publishing a new version flags every affected user for blocking re-acceptance on next login. Body seeds from the platform template; edit clauses first if needed.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="publish-role" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background">
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
              <option value="cam_vendor">CAM Vendor</option>
              <option value="privacy">Privacy Policy</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">New version</span>
            <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.1" data-testid="publish-version" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono" />
          </label>
        </div>
        <label className="block mt-3">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Changelog (shown to admin only)</span>
          <textarea value={form.changelog} onChange={(e) => setForm({ ...form, changelog: e.target.value })} rows={2} data-testid="publish-changelog" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
        </label>
        <button onClick={publish} disabled={publishing} data-testid="publish-submit" className="mt-4 bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {publishing && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Publish & trigger re-acceptance
        </button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Version</th>
              <th className="text-left px-4 py-3">Published</th>
              <th className="text-left px-4 py-3">Hash</th>
              <th className="text-left px-4 py-3">Changelog</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No versions published yet.</td></tr>}
            {rows && rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-4 py-3 capitalize">{r.role.replace("_", " ")}</td>
                <td className="px-4 py-3 font-mono text-xs">v{r.version}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.published_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">{r.body_hash?.slice(0, 16)}…</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{r.changelog || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AcceptanceAudit() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [detail, setDetail] = useState(null);   // { id, snapshot, meta }
  const load = () => {
    const p = {};
    if (q) p.q = q;
    if (role) p.role = role;
    api.get("/admin/terms/acceptances", { params: p }).then((r) => setRows(r.data));
  };
  useEffect(load, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/terms/acceptance/${id}`);
      setDetail(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="acceptance-audit">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Search user (name / email)</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search" className="mt-1 border border-border rounded-md p-2 text-sm bg-background" />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Filter role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="audit-role" className="mt-1 border border-border rounded-md p-2 text-sm bg-background">
            <option value="">All</option>
            <option value="buyer">Buyer</option>
            <option value="seller">Seller</option>
            <option value="cam_vendor">CAM Vendor</option>
            <option value="privacy">Privacy</option>
          </select>
        </label>
        <button onClick={load} data-testid="audit-search-btn" className="border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary">Search</button>
      </div>
      <p className="text-xs text-muted-foreground">
        Each row is an immutable acceptance record: user id, exact version, timestamp, IP, and the full text they consented to. Use "View" to open the exact snapshot for legal disputes.
      </p>
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Doc / Version</th>
              <th className="text-left px-4 py-3">Event</th>
              <th className="text-left px-4 py-3">Accepted</th>
              <th className="text-left px-4 py-3">IP</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No records found.</td></tr>}
            {rows && rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.user_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.user_email || r.user_id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{r.terms_role} · v{r.terms_version}</td>
                <td className="px-4 py-3 text-xs">{r.event}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.accepted_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.ip}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openDetail(r.id)}
                    data-testid={`audit-view-${r.id}`}
                    className="text-xs border border-border rounded-md px-2.5 py-1 hover:bg-secondary"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Acceptance record</p>
                <h3 className="font-display font-bold text-lg mt-1">{detail.terms_role} · v{detail.terms_version}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-foreground" data-testid="audit-close"><XCircle className="h-5 w-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 text-xs font-mono border-b border-border">
              <div><span className="text-muted-foreground uppercase tracking-wider block">User ID</span>{detail.user_id}</div>
              <div><span className="text-muted-foreground uppercase tracking-wider block">Event</span>{detail.event}</div>
              <div><span className="text-muted-foreground uppercase tracking-wider block">Accepted at</span>{detail.accepted_at?.replace("T", " ").slice(0, 19)}</div>
              <div><span className="text-muted-foreground uppercase tracking-wider block">IP</span>{detail.ip}</div>
              <div className="col-span-2"><span className="text-muted-foreground uppercase tracking-wider block">User agent</span>{detail.user_agent}</div>
              <div className="col-span-2"><span className="text-muted-foreground uppercase tracking-wider block">Checkboxes ticked</span>{(detail.checkbox_states || []).join(", ") || "—"}</div>
              <div className="col-span-2"><span className="text-muted-foreground uppercase tracking-wider block">Body hash (SHA-256)</span>{detail.body_hash}</div>
            </div>
            <div className="p-5 max-h-[50vh] overflow-y-auto">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Exact text the user agreed to</p>
              <pre className="whitespace-pre-wrap text-xs font-mono bg-secondary/30 border border-border rounded-md p-3">{detail.body_snapshot}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GrievanceInbox() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => {
    const p = filter ? { status: filter } : {};
    api.get("/admin/grievances", { params: p }).then((r) => setRows(r.data));
  };
  useEffect(load, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/grievances/${id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="grievance-inbox">
      <div className="flex gap-2">
        {["", "open", "acknowledged", "resolved", "closed"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`grievance-filter-${s || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-md border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
          >
            {s ? s : "All"}
          </button>
        ))}
      </div>
      {rows === null && <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>}
      {rows && rows.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">No grievances.</div>}
      {rows && rows.map((g) => (
        <div key={g.id} className="border border-border rounded-md bg-card p-4" data-testid={`grievance-${g.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-sm">{g.subject}</p>
            <StatusTag status={g.status} />
          </div>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            <Mail className="h-3 w-3 inline mr-1" /> {g.email}
            {g.phone && <><span className="mx-2">·</span><Phone className="h-3 w-3 inline mr-1" /> {g.phone}</>}
            <span className="mx-2">·</span>{g.created_at?.slice(0, 19).replace("T", " ")}
          </p>
          <p className="text-sm mt-3 leading-relaxed whitespace-pre-wrap">{g.message}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {g.status !== "acknowledged" && <button onClick={() => setStatus(g.id, "acknowledged")} className="text-xs border border-border rounded-md px-3 py-1 hover:bg-secondary">Acknowledge</button>}
            {g.status !== "resolved" && <button onClick={() => setStatus(g.id, "resolved")} className="text-xs border border-emerald-500/30 text-emerald-600 rounded-md px-3 py-1 hover:bg-emerald-500/10">Mark resolved</button>}
            {g.status !== "closed" && <button onClick={() => setStatus(g.id, "closed")} className="text-xs border border-border rounded-md px-3 py-1 hover:bg-secondary">Close</button>}
          </div>
        </div>
      ))}
    </div>
  );
}
